/**
 * Detect document page corners (TL, TR, BR, BL) from a phone photo.
 *
 * Finds the four page corners (perspective quad), not an axis-aligned box.
 *
 * Primary (v13, Fable-protected, see "Step-edge sheet detector" below):
 * glyph bbox → per-side straight-line search for the innermost clean
 * paper↔desk step → intersect the four lines.
 * Fallback (v12): paper outline → convex hull → Douglas–Peucker → 4 vertices,
 * then refine each corner by intersecting fitted edge lines. AABB last.
 *
 * Design constraints (calibration set + product):
 * - NOT A4-only: portrait A4, landscape sheets, other office sizes, and
 *   narrow/wide thermal receipt paper must all be accepted.
 * - Aspect is whatever the sheet is (roughly ~0.2–4+ width/height in-frame).
 * - Corners may be creased/bent — prefer outermost hull tips over an ideal
 *   rectangle; do not force parallelogram / equal opposite sides.
 * - Ranking rewards ink capture + perspective, never “looks like A4”.
 */

function toGray(data, w, h) {
  const gray = new Float32Array(w * h);
  for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
    gray[p] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  }
  return gray;
}

function boxBlur(gray, w, h, radius = 1) {
  const out = new Float32Array(w * h);
  const r = Math.max(1, radius | 0);
  const tmp = new Float32Array(w * h);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      let sum = 0;
      let n = 0;
      for (let dx = -r; dx <= r; dx += 1) {
        const xx = x + dx;
        if (xx < 0 || xx >= w) continue;
        sum += gray[y * w + xx];
        n += 1;
      }
      tmp[y * w + x] = sum / n;
    }
  }
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      let sum = 0;
      let n = 0;
      for (let dy = -r; dy <= r; dy += 1) {
        const yy = y + dy;
        if (yy < 0 || yy >= h) continue;
        sum += tmp[yy * w + x];
        n += 1;
      }
      out[y * w + x] = sum / n;
    }
  }
  return out;
}

function localVariance(gray, w, h, radius = 2) {
  const g2 = new Float32Array(w * h);
  for (let i = 0; i < gray.length; i += 1) g2[i] = gray[i] * gray[i];
  const mean = boxBlur(gray, w, h, radius);
  const meanSq = boxBlur(g2, w, h, radius);
  const out = new Float32Array(w * h);
  for (let i = 0; i < out.length; i += 1) {
    out[i] = Math.max(0, meanSq[i] - mean[i] * mean[i]);
  }
  return out;
}

function sobelMagnitude(gray, w, h) {
  const out = new Float32Array(w * h);
  for (let y = 1; y < h - 1; y += 1) {
    for (let x = 1; x < w - 1; x += 1) {
      const i = y * w + x;
      const gx =
        -gray[i - w - 1] +
        gray[i - w + 1] -
        2 * gray[i - 1] +
        2 * gray[i + 1] -
        gray[i + w - 1] +
        gray[i + w + 1];
      const gy =
        -gray[i - w - 1] -
        2 * gray[i - w] -
        gray[i - w + 1] +
        gray[i + w - 1] +
        2 * gray[i + w] +
        gray[i + w + 1];
      out[i] = Math.hypot(gx, gy);
    }
  }
  return out;
}

function otsuThreshold(gray) {
  const hist = new Array(256).fill(0);
  for (let i = 0; i < gray.length; i += 1) {
    hist[Math.max(0, Math.min(255, Math.round(gray[i])))] += 1;
  }
  const total = gray.length;
  let sumAll = 0;
  for (let i = 0; i < 256; i += 1) sumAll += i * hist[i];
  let sumB = 0;
  let wB = 0;
  let best = 0;
  let bestVar = -1;
  for (let t = 0; t < 256; t += 1) {
    wB += hist[t];
    if (wB === 0) continue;
    const wF = total - wB;
    if (wF === 0) break;
    sumB += t * hist[t];
    const mB = sumB / wB;
    const mF = (sumAll - sumB) / wF;
    const between = wB * wF * (mB - mF) * (mB - mF);
    if (between > bestVar) {
      bestVar = between;
      best = t;
    }
  }
  return best;
}

function percentile(arr, p) {
  const vals = Array.from(arr);
  vals.sort((a, b) => a - b);
  const idx = Math.min(vals.length - 1, Math.max(0, Math.floor(vals.length * p)));
  return vals[idx];
}

function morphOpenClose(mask, w, h) {
  const erode = (src) => {
    const dst = new Uint8Array(w * h);
    for (let y = 1; y < h - 1; y += 1) {
      for (let x = 1; x < w - 1; x += 1) {
        let ok = 1;
        for (let dy = -1; dy <= 1 && ok; dy += 1) {
          for (let dx = -1; dx <= 1; dx += 1) {
            if (!src[(y + dy) * w + (x + dx)]) {
              ok = 0;
              break;
            }
          }
        }
        dst[y * w + x] = ok;
      }
    }
    return dst;
  };
  const dilate = (src) => {
    const dst = new Uint8Array(w * h);
    for (let y = 1; y < h - 1; y += 1) {
      for (let x = 1; x < w - 1; x += 1) {
        let ok = 0;
        for (let dy = -1; dy <= 1 && !ok; dy += 1) {
          for (let dx = -1; dx <= 1; dx += 1) {
            if (src[(y + dy) * w + (x + dx)]) {
              ok = 1;
              break;
            }
          }
        }
        dst[y * w + x] = ok;
      }
    }
    return dst;
  };
  return erode(dilate(dilate(erode(mask))));
}

function largestComponent(mask, w, h) {
  const labels = new Int32Array(w * h);
  let bestSize = 0;
  let bestLabel = 0;
  let next = 1;
  const qx = new Int32Array(w * h);
  const qy = new Int32Array(w * h);

  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const i = y * w + x;
      if (!mask[i] || labels[i]) continue;
      const lab = next;
      next += 1;
      let size = 0;
      let head = 0;
      let tail = 0;
      qx[tail] = x;
      qy[tail] = y;
      tail += 1;
      labels[i] = lab;
      while (head < tail) {
        const cx = qx[head];
        const cy = qy[head];
        head += 1;
        size += 1;
        const neigh = [
          [cx - 1, cy],
          [cx + 1, cy],
          [cx, cy - 1],
          [cx, cy + 1],
        ];
        for (const [nx, ny] of neigh) {
          if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
          const ni = ny * w + nx;
          if (!mask[ni] || labels[ni]) continue;
          labels[ni] = lab;
          qx[tail] = nx;
          qy[tail] = ny;
          tail += 1;
        }
      }
      if (size > bestSize) {
        bestSize = size;
        bestLabel = lab;
      }
    }
  }
  if (!bestLabel || bestSize < 64) return null;
  return { labels, bestLabel, size: bestSize };
}

function componentMeanGray(gray, labels, bestLabel) {
  let sum = 0;
  let n = 0;
  for (let i = 0; i < labels.length; i += 1) {
    if (labels[i] === bestLabel) {
      sum += gray[i];
      n += 1;
    }
  }
  return n ? sum / n : 0;
}

function componentBoundaryPoints(labels, bestLabel, w, h) {
  const pts = [];
  for (let y = 1; y < h - 1; y += 1) {
    for (let x = 1; x < w - 1; x += 1) {
      if (labels[y * w + x] !== bestLabel) continue;
      const border =
        labels[(y - 1) * w + x] !== bestLabel ||
        labels[(y + 1) * w + x] !== bestLabel ||
        labels[y * w + (x - 1)] !== bestLabel ||
        labels[y * w + (x + 1)] !== bestLabel;
      if (border) pts.push({ x, y });
    }
  }
  return pts;
}

function extremalCorners(pts) {
  if (!pts.length) return null;
  let tl = pts[0];
  let tr = pts[0];
  let br = pts[0];
  let bl = pts[0];
  let tlScore = tl.x + tl.y;
  let trScore = tr.x - tr.y;
  let brScore = br.x + br.y;
  let blScore = -bl.x + bl.y;
  for (const p of pts) {
    const sTl = p.x + p.y;
    const sTr = p.x - p.y;
    const sBr = p.x + p.y;
    const sBl = -p.x + p.y;
    if (sTl < tlScore) {
      tlScore = sTl;
      tl = p;
    }
    if (sTr > trScore) {
      trScore = sTr;
      tr = p;
    }
    if (sBr > brScore) {
      brScore = sBr;
      br = p;
    }
    if (sBl > blScore) {
      blScore = sBl;
      bl = p;
    }
  }
  return [
    { x: tl.x, y: tl.y },
    { x: tr.x, y: tr.y },
    { x: br.x, y: br.y },
    { x: bl.x, y: bl.y },
  ];
}

function cross2(o, a, b) {
  return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
}

/** Monotone-chain convex hull. Returns CCW vertices. */
function convexHull(pts) {
  if (pts.length < 3) return pts.slice();
  const p = pts
    .map((q) => ({ x: q.x, y: q.y }))
    .sort((a, b) => a.x - b.x || a.y - b.y);
  const lower = [];
  for (const pt of p) {
    while (
      lower.length >= 2 &&
      cross2(lower[lower.length - 2], lower[lower.length - 1], pt) <= 0
    ) {
      lower.pop();
    }
    lower.push(pt);
  }
  const upper = [];
  for (let i = p.length - 1; i >= 0; i -= 1) {
    const pt = p[i];
    while (
      upper.length >= 2 &&
      cross2(upper[upper.length - 2], upper[upper.length - 1], pt) <= 0
    ) {
      upper.pop();
    }
    upper.push(pt);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

function perpDistToSegment(p, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  return Math.abs(dy * p.x - dx * p.y + b.x * a.y - b.y * a.x) / len;
}

/** Open polyline Ramer–Douglas–Peucker. */
function approxPolyDP(pts, eps) {
  if (pts.length < 3) return pts.slice();
  let maxD = 0;
  let idx = 0;
  const a = pts[0];
  const b = pts[pts.length - 1];
  for (let i = 1; i < pts.length - 1; i += 1) {
    const d = perpDistToSegment(pts[i], a, b);
    if (d > maxD) {
      maxD = d;
      idx = i;
    }
  }
  if (maxD > eps) {
    const left = approxPolyDP(pts.slice(0, idx + 1), eps);
    const right = approxPolyDP(pts.slice(idx), eps);
    return left.slice(0, -1).concat(right);
  }
  return [a, b];
}

/**
 * Reduce a paper outline hull to exactly four corner vertices.
 * Searches Douglas–Peucker epsilon until we land on a 4-gon.
 */
function hullToFourCorners(hull, frameW, frameH) {
  if (!hull || hull.length < 4) return extremalCorners(hull || []);
  const diag = Math.hypot(frameW, frameH);
  let lo = diag * 0.004;
  let hi = diag * 0.28;
  let best4 = null;
  let closest = hull;
  for (let iter = 0; iter < 28; iter += 1) {
    const mid = (lo + hi) / 2;
    const ring = hull.concat([hull[0]]);
    let verts = approxPolyDP(ring, mid);
    if (
      verts.length > 1 &&
      dist(verts[0], verts[verts.length - 1]) < 1.5
    ) {
      verts = verts.slice(0, -1);
    }
    if (verts.length === 4) {
      best4 = verts;
      break;
    }
    if (Math.abs(verts.length - 4) < Math.abs(closest.length - 4)) {
      closest = verts;
    }
    if (verts.length > 4) lo = mid;
    else hi = mid;
  }
  const verts = best4 || closest;
  if (verts.length === 4) return extremalCorners(verts);
  // Too many vertices left — keep the four extremal ones on the hull.
  return extremalCorners(hull);
}

/**
 * Fit ax+by+c=0 through points near a side (RANSAC), then re-intersect
 * adjacent sides so corners sit on the real page edges.
 * Prefers the outermost edge (page border) — not strong internal table rules.
 */
function refineCornersByEdgeLines(corners, mag, w, h) {
  if (!corners || corners.length !== 4) return corners;
  const edgeThr = Math.max(10, percentile(mag, 0.86));
  const band = Math.max(5, Math.round(Math.min(w, h) * 0.04));
  const cx0 = corners.reduce((s, p) => s + p.x, 0) / 4;
  const cy0 = corners.reduce((s, p) => s + p.y, 0) / 4;
  const maxMove = Math.hypot(w, h) * 0.06;

  function samplesAlong(a, b) {
    const pts = [];
    const n = Math.max(8, Math.round(dist(a, b) / 2));
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    // Unit normal; flip so +s points outward from page centroid.
    let nx = -dy / len;
    let ny = dx / len;
    const midX = (a.x + b.x) * 0.5;
    const midY = (a.y + b.y) * 0.5;
    if (nx * (midX - cx0) + ny * (midY - cy0) < 0) {
      nx = -nx;
      ny = -ny;
    }
    for (let i = 0; i <= n; i += 1) {
      const t = i / n;
      const px = a.x + dx * t;
      const py = a.y + dy * t;
      // Walk inside → outside; keep the outermost strong edge.
      let best = null;
      for (let s = -Math.floor(band * 0.35); s <= band; s += 1) {
        const x = Math.round(px + nx * s);
        const y = Math.round(py + ny * s);
        if (x < 1 || y < 1 || x >= w - 1 || y >= h - 1) continue;
        if (mag[y * w + x] >= edgeThr) best = { x, y };
      }
      if (best) pts.push(best);
    }
    return pts;
  }

  function fitLine(pts) {
    if (pts.length < 8) return null;
    let bestIn = 0;
    let best = null;
    // Deterministic pair sampling (stable in tests / rebuilds).
    const step = Math.max(1, Math.floor(pts.length / 24));
    for (let trial = 0; trial < 48; trial += 1) {
      const i1 = (trial * step) % pts.length;
      const i2 = (i1 + Math.max(3, Math.floor(pts.length / 5))) % pts.length;
      const p1 = pts[i1];
      const p2 = pts[i2];
      let a = p1.y - p2.y;
      let b = p2.x - p1.x;
      let c = -(a * p1.x + b * p1.y);
      const nrm = Math.hypot(a, b);
      if (nrm < 1e-6) continue;
      a /= nrm;
      b /= nrm;
      c /= nrm;
      let inn = 0;
      for (const p of pts) {
        if (Math.abs(a * p.x + b * p.y + c) < 2.2) inn += 1;
      }
      if (inn > bestIn) {
        bestIn = inn;
        // Refit with inliers via centroid + PCA direction
        let sx = 0;
        let sy = 0;
        let n = 0;
        for (const p of pts) {
          if (Math.abs(a * p.x + b * p.y + c) >= 2.2) continue;
          sx += p.x;
          sy += p.y;
          n += 1;
        }
        if (n < 4) continue;
        const mx = sx / n;
        const my = sy / n;
        let sxx = 0;
        let sxy = 0;
        let syy = 0;
        for (const p of pts) {
          if (Math.abs(a * p.x + b * p.y + c) >= 2.2) continue;
          const dx = p.x - mx;
          const dy = p.y - my;
          sxx += dx * dx;
          sxy += dx * dy;
          syy += dy * dy;
        }
        // Normal = eigenvector of smallest eigenvalue of cov
        const ang = 0.5 * Math.atan2(2 * sxy, sxx - syy);
        // Direction along line
        const ux = Math.cos(ang);
        const uy = Math.sin(ang);
        // Normal perpendicular
        let na = -uy;
        let nb = ux;
        let nc = -(na * mx + nb * my);
        const nn = Math.hypot(na, nb) || 1;
        best = { a: na / nn, b: nb / nn, c: nc / nn };
      }
    }
    return best;
  }

  function intersect(L1, L2) {
    if (!L1 || !L2) return null;
    const det = L1.a * L2.b - L2.a * L1.b;
    if (Math.abs(det) < 1e-8) return null;
    return {
      x: (L2.b * L1.c - L1.b * L2.c) / det,
      y: (L1.a * L2.c - L2.a * L1.c) / det,
    };
  }

  // Sides: 0=top TL→TR, 1=right TR→BR, 2=bottom BR→BL, 3=left BL→TL
  const [tl, tr, br, bl] = corners;
  const sidePts = [
    samplesAlong(tl, tr),
    samplesAlong(tr, br),
    samplesAlong(br, bl),
    samplesAlong(bl, tl),
  ];
  const lines = sidePts.map(fitLine);
  if (lines.filter(Boolean).length < 3) return corners;

  const next = [
    intersect(lines[3], lines[0]) || tl,
    intersect(lines[0], lines[1]) || tr,
    intersect(lines[1], lines[2]) || br,
    intersect(lines[2], lines[3]) || bl,
  ];
  // Reject refinements that jump onto internal structure or off-page.
  for (let i = 0; i < 4; i += 1) {
    if (dist(next[i], corners[i]) > maxMove) next[i] = corners[i];
  }
  if (!isConvexQuad(next)) return corners;
  const refined = clampCorners(next, w, h);
  if (!scoreDetection(refined, null, w, h, null).ok) return corners;
  // Keep refine only if area stays in the same ballpark (not collapsed).
  const a0 = polygonArea(corners);
  const a1 = polygonArea(refined);
  if (a1 < a0 * 0.7 || a1 > a0 * 1.35) return corners;
  return refined;
}

function pointInConvexQuad(x, y, corners) {
  // Same-side test for convex TL,TR,BR,BL
  let sign = 0;
  for (let i = 0; i < 4; i += 1) {
    const a = corners[i];
    const b = corners[(i + 1) % 4];
    const z = (b.x - a.x) * (y - a.y) - (b.y - a.y) * (x - a.x);
    if (Math.abs(z) < 1e-6) continue;
    const s = z > 0 ? 1 : -1;
    if (sign === 0) sign = s;
    else if (s !== sign) return false;
  }
  return true;
}

function polygonArea(pts) {
  let a = 0;
  for (let i = 0; i < pts.length; i += 1) {
    const j = (i + 1) % pts.length;
    a += pts[i].x * pts[j].y - pts[j].x * pts[i].y;
  }
  return Math.abs(a) / 2;
}

function isConvexQuad(pts) {
  if (pts.length !== 4) return false;
  let sign = 0;
  for (let i = 0; i < 4; i += 1) {
    const p0 = pts[i];
    const p1 = pts[(i + 1) % 4];
    const p2 = pts[(i + 2) % 4];
    const z = (p1.x - p0.x) * (p2.y - p1.y) - (p1.y - p0.y) * (p2.x - p1.x);
    if (Math.abs(z) < 1e-3) return false;
    const s = z > 0 ? 1 : -1;
    if (sign === 0) sign = s;
    else if (s !== sign) return false;
  }
  return true;
}

function clampCorners(corners, w, h, pad = 1) {
  return corners.map((p) => ({
    x: Math.min(w - 1 - pad, Math.max(pad, p.x)),
    y: Math.min(h - 1 - pad, Math.max(pad, p.y)),
  }));
}

function dist(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function borderTouchCount(corners, frameW, frameH) {
  const pad = Math.min(frameW, frameH) * 0.03;
  return corners.filter(
    (p) =>
      p.x <= pad ||
      p.y <= pad ||
      p.x >= frameW - 1 - pad ||
      p.y >= frameH - 1 - pad
  ).length;
}

function scoreDetection(corners, blobMean, frameW, frameH, blobSize) {
  const frame = frameW * frameH;
  const area = polygonArea(corners);
  const areaRatio = area / frame;
  const blobRatio = blobSize != null ? blobSize / frame : areaRatio;
  const [tl, tr, br, bl] = corners;
  const top = dist(tl, tr);
  const right = dist(tr, br);
  const bottom = dist(br, bl);
  const left = dist(bl, tl);
  const aspect = Math.max(top, bottom) / Math.max(1, Math.max(left, right));

  if (!isConvexQuad(corners)) return { ok: false, reason: "not_convex" };
  // Reject loose near-full-frame boxes (white-desk false expand).
  // Floor is low so small thermal slips still pass.
  if (areaRatio < 0.06 || areaRatio > 0.82) return { ok: false, reason: "area" };
  if (blobRatio < 0.05) return { ok: false, reason: "blob_small" };
  if (blobMean != null && blobMean < 115) return { ok: false, reason: "too_dark" };
  // Wide range: thermal stubs → landscape sheets (not A4-only).
  if (aspect < 0.18 || aspect > 4.5) return { ok: false, reason: "aspect" };
  if (top < frameW * 0.08 || bottom < frameW * 0.08) return { ok: false, reason: "narrow" };
  if (left < frameH * 0.08 || right < frameH * 0.08) return { ok: false, reason: "short" };

  const onBorder = borderTouchCount(corners, frameW, frameH);
  // Only reject when the quad essentially is the photo frame.
  if (onBorder >= 3 && areaRatio > 0.78) return { ok: false, reason: "frame_hug" };
  if (onBorder >= 4) return { ok: false, reason: "frame_hug" };

  return { ok: true, areaRatio, blobMean };
}

/**
 * Morph-close that only grows into bright pixels (paper), never into dark
 * floor / legs / shadows that otherwise glue onto the page blob.
 */
function morphCloseBrightOnly(mask, gray, w, h, radius = 2, brightMin = 0) {
  const r = Math.max(1, radius | 0);
  const allow = (x, y) => {
    if (!gray || brightMin <= 0) return true;
    return gray[y * w + x] >= brightMin;
  };
  const dilate = (src) => {
    const dst = new Uint8Array(w * h);
    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < w; x += 1) {
        if (!allow(x, y)) continue;
        let ok = 0;
        for (let dy = -r; dy <= r && !ok; dy += 1) {
          for (let dx = -r; dx <= r; dx += 1) {
            const xx = x + dx;
            const yy = y + dy;
            if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
            if (src[yy * w + xx]) {
              ok = 1;
              break;
            }
          }
        }
        dst[y * w + x] = ok;
      }
    }
    return dst;
  };
  const erode = (src) => {
    const dst = new Uint8Array(w * h);
    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < w; x += 1) {
        let ok = 1;
        for (let dy = -r; dy <= r && ok; dy += 1) {
          for (let dx = -r; dx <= r; dx += 1) {
            const xx = x + dx;
            const yy = y + dy;
            if (xx < 0 || yy < 0 || xx >= w || yy >= h || !src[yy * w + xx]) {
              ok = 0;
              break;
            }
          }
        }
        dst[y * w + x] = ok;
      }
    }
    return dst;
  };
  return erode(dilate(dilate(mask)));
}

/** True when a dark pixel sits on bright paper (not a dark desk / key strip). */
function inkSurroundedByPaper(gray, w, h, x, y, paperFloor) {
  const samples = [];
  for (let dy = -5; dy <= 5; dy += 2) {
    for (let dx = -5; dx <= 5; dx += 2) {
      if (dx === 0 && dy === 0) continue;
      const xx = x + dx;
      const yy = y + dy;
      if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
      samples.push(gray[yy * w + xx]);
    }
  }
  if (samples.length < 8) return false;
  samples.sort((a, b) => a - b);
  // Median must look like paper — rejects dark-key strips next to a few bright keys.
  const median = samples[samples.length >> 1];
  if (median < paperFloor) return false;
  let bright = 0;
  for (const v of samples) if (v >= paperFloor) bright += 1;
  return bright >= Math.ceil(samples.length * 0.55);
}

function buildInkOnPaperMask(gray, w, h) {
  const inkCut = Math.min(155, Math.max(90, percentile(gray, 0.2)));
  const paperFloor = Math.max(inkCut + 25, percentile(gray, 0.45));
  const mask = new Uint8Array(w * h);
  let n = 0;
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      if (gray[y * w + x] > inkCut) continue;
      if (!inkSurroundedByPaper(gray, w, h, x, y, paperFloor)) continue;
      mask[y * w + x] = 1;
      n += 1;
    }
  }
  return { mask, n, inkCut, paperFloor };
}

/**
 * Four page corners from a paper component outline (not an AABB).
 * Convex hull → Douglas–Peucker → TL/TR/BR/BL.
 */
function perspectiveCornersFromComponent(labels, bestLabel, w, h, blobMean) {
  const boundary = componentBoundaryPoints(labels, bestLabel, w, h);
  if (boundary.length < 24) return null;
  const hull = convexHull(boundary);
  if (hull.length < 4) return null;
  let corners = hullToFourCorners(hull, w, h);
  if (!corners) return null;
  corners = clampCorners(corners, w, h);
  if (!isConvexQuad(corners)) return null;
  const scored = scoreDetection(corners, blobMean, w, h, null);
  if (!scored.ok) return null;
  return corners;
}

/**
 * Build a paper-ish mask: ink + textured print, grown into bright blank
 * margins only (never into dark legs / floor).
 */
function buildGrownPaperComponent(gray, variance, w, h) {
  const ink = buildInkOnPaperMask(gray, w, h);
  const gCut = Math.max(175, percentile(gray, 0.45));
  const vCut = Math.max(4, percentile(variance, 0.55) * 0.4);
  const seed = new Uint8Array(w * h);
  let seedN = 0;
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const i = y * w + x;
      const g = gray[i];
      const v = variance[i];
      if (ink.mask[i] || (g >= gCut && v >= vCut)) {
        seed[i] = 1;
        seedN += 1;
      }
    }
  }
  if (seedN < w * h * 0.01 || seedN > w * h * 0.55) {
    return { ink, seedN: 0, comp: null, brightMin: 0 };
  }
  const brightMin = Math.max(ink.paperFloor - 8, percentile(gray, 0.42));
  // Extra grow into blank white page margins (logos, headers) — still blocked
  // from dark floor by brightMin.
  const grown = morphCloseBrightOnly(
    morphCloseBrightOnly(seed, gray, w, h, 3, brightMin),
    gray,
    w,
    h,
    2,
    brightMin
  );
  const comp = largestComponent(grown, w, h);
  return { ink, seedN, comp, brightMin };
}

/**
 * Invoice on a light desk: four corners from the grown paper outline.
 */
function detectViaTexturedPaper(gray, variance, w, h) {
  const { seedN, comp } = buildGrownPaperComponent(gray, variance, w, h);
  if (!comp) return null;

  const blobMean = componentMeanGray(gray, comp.labels, comp.bestLabel);
  const corners = perspectiveCornersFromComponent(
    comp.labels,
    comp.bestLabel,
    w,
    h,
    blobMean
  );
  if (!corners) return null;

  const scored = scoreDetection(corners, blobMean, w, h, comp.size);
  if (!scored.ok) return null;
  const inkCore = seedN / Math.max(1, polygonArea(corners));
  if (inkCore < 0.035 && scored.areaRatio > 0.55) return null;
  return corners;
}

/**
 * Fallback: perspective corners from ink-on-paper grown into bright margins.
 */
function detectViaInkBounds(gray, w, h) {
  const ink = buildInkOnPaperMask(gray, w, h);
  const frac = ink.n / (w * h);
  if (frac < 0.012 || frac > 0.4) return null;

  const brightMin = Math.max(ink.paperFloor - 8, percentile(gray, 0.42));
  const grown = morphCloseBrightOnly(ink.mask, gray, w, h, 3, brightMin);
  const comp = largestComponent(grown, w, h);
  if (!comp) return null;
  const blobMean = componentMeanGray(gray, comp.labels, comp.bestLabel);
  let corners = perspectiveCornersFromComponent(
    comp.labels,
    comp.bestLabel,
    w,
    h,
    blobMean
  );
  if (!corners) return null;
  const scored = scoreDetection(corners, blobMean, w, h, ink.n);
  if (!scored.ok) return null;
  return corners;
}

/**
 * Axis-aligned page from strong edge projections (fallback).
 * Uses outer percentiles so internal table lines do not define the crop.
 */
function detectViaEdgeProjection(gray, w, h) {
  const mag = sobelMagnitude(gray, w, h);
  const thr = Math.max(8, percentile(mag, 0.85));
  const brightFloor = Math.max(140, percentile(gray, 0.3));

  const lefts = [];
  const rights = [];
  const y0 = Math.floor(h * 0.08);
  const y1 = Math.floor(h * 0.92);
  for (let y = y0; y < y1; y += 1) {
    const xs = [];
    for (let x = 1; x < w - 1; x += 1) {
      const i = y * w + x;
      if (mag[i] >= thr && gray[i] >= brightFloor) xs.push(x);
    }
    if (xs.length < 4) continue;
    const span = xs[xs.length - 1] - xs[0];
    if (span < w * 0.2 || span > w * 0.96) continue;
    lefts.push(xs[0]);
    rights.push(xs[xs.length - 1]);
  }

  const tops = [];
  const bottoms = [];
  const x0 = Math.floor(w * 0.04);
  const x1 = Math.floor(w * 0.96);
  for (let x = x0; x < x1; x += 1) {
    const ys = [];
    for (let y = 1; y < h - 1; y += 1) {
      const i = y * w + x;
      if (mag[i] >= thr && gray[i] >= brightFloor) ys.push(y);
    }
    if (ys.length < 4) continue;
    const span = ys[ys.length - 1] - ys[0];
    if (span < h * 0.15 || span > h * 0.96) continue;
    tops.push(ys[0]);
    bottoms.push(ys[ys.length - 1]);
  }

  if (lefts.length < 10 || rights.length < 10 || tops.length < 10 || bottoms.length < 10) {
    return null;
  }

  const L = percentile(lefts, 0.12);
  const R = percentile(rights, 0.88);
  const T = percentile(tops, 0.12);
  const B = percentile(bottoms, 0.88);
  if (L == null || R == null || T == null || B == null) return null;
  if (R - L < w * 0.25 || B - T < h * 0.18) return null;

  const padX = Math.max(1, (R - L) * 0.008);
  const padY = Math.max(1, (B - T) * 0.008);
  const corners = [
    { x: L + padX, y: T + padY },
    { x: R - padX, y: T + padY },
    { x: R - padX, y: B - padY },
    { x: L + padX, y: B - padY },
  ];
  const scored = scoreDetection(corners, null, w, h, null);
  if (!scored.ok) return null;
  return clampCorners(corners, w, h);
}

/**
 * Paper mask for dark-desk photos: bright + textured.
 * Uses a high brightness cut so light desks do not swallow the frame.
 */
function buildPaperMask(gray, variance, w, h) {
  const otsu = otsuThreshold(gray);
  // Prefer the bright peak — paper on a light desk needs a high cut.
  const brightCut = Math.max(otsu + 8, percentile(gray, 0.62), percentile(gray, 0.5) + 12);
  const varCut = Math.max(6, percentile(variance, 0.6));
  const meanGray = gray.reduce((s, v) => s + v, 0) / Math.max(1, gray.length);
  const veryBright = Math.max(brightCut + 6, percentile(gray, 0.78));

  const mask = new Uint8Array(w * h);
  for (let i = 0; i < mask.length; i += 1) {
    const g = gray[i];
    const v = variance[i];
    const bright = g >= brightCut;
    const paperish =
      bright && (v >= varCut * 0.4 || g >= veryBright || g >= meanGray + 28);
    if (paperish && g > 110) mask[i] = 1;
  }
  return morphOpenClose(mask, w, h);
}

function detectViaBlob(gray, variance, w, h) {
  const mask = buildPaperMask(gray, variance, w, h);
  const comp = largestComponent(mask, w, h);
  if (!comp) return null;

  const blobMean = componentMeanGray(gray, comp.labels, comp.bestLabel);
  const boundary = componentBoundaryPoints(comp.labels, comp.bestLabel, w, h);
  if (boundary.length < 16) return null;

  let corners = extremalCorners(boundary);
  if (!corners) return null;
  corners = clampCorners(corners, w, h);

  const scored = scoreDetection(corners, blobMean, w, h, comp.size);
  if (!scored.ok) return null;
  return corners;
}

function isNearAxisAligned(corners, tolPx = 4) {
  if (!corners || corners.length !== 4) return true;
  const [tl, tr, br, bl] = corners;
  return (
    Math.abs(tl.y - tr.y) <= tolPx &&
    Math.abs(bl.y - br.y) <= tolPx &&
    Math.abs(tl.x - bl.x) <= tolPx &&
    Math.abs(tr.x - br.x) <= tolPx
  );
}

/**
 * Cast rays from ink centroid; keep the farthest strong page-edge hit per angle.
 * Builds a true perspective outline even when desk ≈ paper brightness.
 */
function detectViaRadialEdges(gray, mag, inkMask, w, h) {
  let sx = 0;
  let sy = 0;
  let n = 0;
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      if (!inkMask[y * w + x]) continue;
      sx += x;
      sy += y;
      n += 1;
    }
  }
  if (n < 64) return null;
  const cx = sx / n;
  const cy = sy / n;
  const maxR = Math.hypot(w, h);
  const edgeThr = Math.max(10, percentile(mag, 0.78));
  const paperFloor = Math.max(150, percentile(gray, 0.4));
  const pts = [];

  for (let a = 0; a < 360; a += 2) {
    const rad = (a * Math.PI) / 180;
    const dx = Math.cos(rad);
    const dy = Math.sin(rad);
    let lastInk = null;
    let bestEdge = null;
    for (let r = 4; r < maxR; r += 1.5) {
      const x = Math.round(cx + dx * r);
      const y = Math.round(cy + dy * r);
      if (x < 1 || y < 1 || x >= w - 1 || y >= h - 1) break;
      const i = y * w + x;
      const g = gray[i];
      const m = mag[i];
      if (inkMask[i] || (g >= paperFloor && m < edgeThr * 1.8)) {
        lastInk = { x, y };
      }
      // First strong page border after paper — do not walk past into desk clutter.
      if (lastInk && m >= edgeThr && !bestEdge) {
        bestEdge = { x, y };
        break;
      }
      // Hard stop on dark legs / floor after leaving paper.
      if (lastInk && g < paperFloor - 35 && m < edgeThr * 0.5) break;
    }
    const hit = bestEdge || lastInk;
    if (hit) pts.push(hit);
  }
  if (pts.length < 24) return null;
  let corners = extremalCorners(pts);
  if (!corners) return null;
  corners = clampCorners(corners, w, h);
  if (!isConvexQuad(corners)) return null;
  const scored = scoreDetection(corners, null, w, h, n);
  if (!scored.ok) return null;
  return corners;
}

/**
 * Pull corners onto nearby edges. Only search sideways / slightly outward —
 * never pull top/bottom inward onto header or table rules (that is what made
 * L/R look fine while top/bottom cut through the page).
 */
function snapCornersToEdges(corners, mag, w, h) {
  const cx = corners.reduce((s, p) => s + p.x, 0) / 4;
  const cy = corners.reduce((s, p) => s + p.y, 0) / 4;
  const radius = Math.max(5, Math.round(Math.min(w, h) * 0.028));
  return corners.map((p, idx) => {
    const ox = p.x - cx;
    const oy = p.y - cy;
    const len = Math.hypot(ox, oy) || 1;
    const ux = ox / len;
    const uy = oy / len;
    const tx = -uy;
    const ty = ux;
    let best = { x: p.x, y: p.y };
    let bestScore = -1;
    // idx 0,1 = top corners; 2,3 = bottom — allow more outward, almost no inward.
    const isTop = idx === 0 || idx === 1;
    const isBot = idx === 2 || idx === 3;
    const outMin = isTop || isBot ? -1 : -Math.floor(radius * 0.35);
    const outMax = Math.floor(radius * (isTop || isBot ? 0.55 : 0.35));
    for (let along = -radius; along <= radius; along += 1) {
      for (let out = outMin; out <= outMax; out += 1) {
        const x = Math.round(p.x + tx * along + ux * out);
        const y = Math.round(p.y + ty * along + uy * out);
        if (x < 1 || y < 1 || x >= w - 1 || y >= h - 1) continue;
        // Slight outward preference for top/bottom corners.
        const score = mag[y * w + x] + (isTop || isBot ? out * 0.12 : -out * 0.05);
        if (score > bestScore) {
          bestScore = score;
          best = { x, y };
        }
      }
    }
    return best;
  });
}

/**
 * Rank a candidate crop: ink capture + perspective. No A4 aspect target —
 * thermal / landscape / creased sheets must compete fairly with A4.
 * Ink membership uses the real quad (not its AABB).
 */
function rankCandidate(corners, w, h, inkMask) {
  if (!corners || corners.length !== 4) return -Infinity;
  const scored = scoreDetection(corners, null, w, h, null);
  if (!scored.ok) return -Infinity;
  const areaRatio = scored.areaRatio;
  if (polygonArea(corners) < 16) return -Infinity;

  let inkIn = 0;
  let inkAll = 0;
  let samplesIn = 0;
  const step = Math.max(1, Math.floor(Math.min(w, h) / 80));
  for (let y = 0; y < h; y += step) {
    for (let x = 0; x < w; x += step) {
      const i = y * w + x;
      const isInk = inkMask && inkMask[i];
      if (isInk) inkAll += 1;
      if (!pointInConvexQuad(x, y, corners)) continue;
      samplesIn += 1;
      if (isInk) inkIn += 1;
    }
  }

  const inkCoverage = inkAll > 0 ? inkIn / inkAll : 0;
  const inkDensity = samplesIn > 0 ? inkIn / samplesIn : 0;
  const border = borderTouchCount(corners, w, h);
  // Broad “reasonable in-frame” band (thermal small → large landscape sheet).
  let areaScore = 1;
  if (areaRatio < 0.12) areaScore = areaRatio / 0.12;
  else if (areaRatio > 0.7) areaScore = Math.max(0, 1 - (areaRatio - 0.7) / 0.2);
  let score =
    areaScore * 0.9 +
    inkCoverage * 3.0 +
    inkDensity * 0.55 -
    border * 0.7;
  if (areaRatio > 0.7) score -= (areaRatio - 0.7) * 4;
  // Two+ frame touches usually means desk overshoot on this photo set.
  if (border >= 2 && areaRatio > 0.55) score -= 1.4;
  // Tiny quads that only cover a column/table are almost always wrong.
  if (areaRatio < 0.1) score -= (0.1 - areaRatio) * 8;
  // Must clear the ink band with blank margin — reject table-height crops.
  const span = inkVerticalSpan(inkMask, w, h);
  if (span) {
    const quadTop = Math.min(corners[0].y, corners[1].y);
    const quadBot = Math.max(corners[2].y, corners[3].y);
    const quadH = Math.max(1, quadBot - quadTop);
    const inkH = Math.max(1, span.bot - span.top);
    const marginFrac = (quadH - inkH) / inkH;
    if (quadH < inkH * 0.98) score -= 2.5;
    else if (marginFrac < 0.04) score -= 1.2;
    else if (marginFrac >= 0.08 && marginFrac <= 0.45) score += 0.7;
    // Ink must not sit flush against the quad top/bottom (missing header/footer).
    if (quadTop > span.top - 2) score -= 0.8;
    if (quadBot < span.bot + 2) score -= 0.8;
  }
  // Reward sheet-sized crops that still capture nearly all ink (blank margins).
  // Without this, a tight table box wins on inkDensity alone.
  if (inkCoverage >= 0.92 && areaRatio >= 0.32 && areaRatio <= 0.62) {
    score += 1.15;
  } else if (inkCoverage >= 0.88 && areaRatio >= 0.28 && areaRatio <= 0.68) {
    score += 0.55;
  }
  // Prefer perspective quads; mild penalty only (creased pages can look nearly aligned).
  if (isNearAxisAligned(corners)) score -= 0.9;
  else score += 0.55;
  return score;
}

function pickBestCorners(candidates, w, h, inkMask) {
  let best = null;
  let bestScore = -Infinity;
  for (const c of candidates) {
    if (!c) continue;
    const s = rankCandidate(c, w, h, inkMask);
    if (s > bestScore) {
      bestScore = s;
      best = c;
    }
  }
  if (bestScore < -0.5) return null;
  return best;
}

/**
 * Ink vertical span — guides how far blank margins may extend beyond print.
 */
function inkVerticalSpan(inkMask, w, h) {
  if (!inkMask) return null;
  let top = h;
  let bot = -1;
  let n = 0;
  const step = Math.max(1, Math.floor(Math.min(w, h) / 120));
  for (let y = 0; y < h; y += step) {
    for (let x = 0; x < w; x += step) {
      if (!inkMask[y * w + x]) continue;
      n += 1;
      if (y < top) top = y;
      if (y > bot) bot = y;
    }
  }
  if (n < 8 || bot < top) return null;
  return { top, bot, n };
}

/** Ink horizontal span — recover L/R when a seed collapses inward. */
function inkHorizontalSpan(inkMask, w, h) {
  if (!inkMask) return null;
  let left = w;
  let right = -1;
  let n = 0;
  const step = Math.max(1, Math.floor(Math.min(w, h) / 120));
  for (let y = 0; y < h; y += step) {
    for (let x = 0; x < w; x += step) {
      if (!inkMask[y * w + x]) continue;
      n += 1;
      if (x < left) left = x;
      if (x > right) right = x;
    }
  }
  if (n < 8 || right < left) return null;
  return { left, right, n };
}

/**
 * Push L/R corners out when the seed sits inside the ink band (missed side margin).
 */
function expandLeftRightToInk(corners, gray, w, h, inkMask) {
  if (!corners || corners.length !== 4) return corners;
  const span = inkHorizontalSpan(inkMask, w, h);
  if (!span) return corners;
  const [tl0, tr0, br0, bl0] = corners;
  const pageW = (dist(tl0, tr0) + dist(bl0, br0)) * 0.5 || w * 0.4;
  const pad = Math.max(3, pageW * 0.04);
  let tl = { ...tl0 };
  let tr = { ...tr0 };
  let br = { ...br0 };
  let bl = { ...bl0 };
  if (tl.x > span.left + 6) tl.x = Math.min(tl.x, span.left - pad);
  if (bl.x > span.left + 6) bl.x = Math.min(bl.x, span.left - pad);
  if (tr.x < span.right - 6) tr.x = Math.max(tr.x, span.right + pad);
  if (br.x < span.right - 6) br.x = Math.max(br.x, span.right + pad);
  // Do not cross into dark desk: back off if gray drops hard.
  const paperFloor = Math.max(150, percentile(gray, 0.42));
  const fixX = (x, y, dir) => {
    let xx = Math.round(x);
    const yy = Math.max(1, Math.min(h - 2, Math.round(y)));
    for (let i = 0; i < 12; i += 1) {
      if (xx < 1 || xx >= w - 1) break;
      if (gray[yy * w + xx] >= paperFloor - 20) break;
      xx -= dir;
    }
    return xx;
  };
  tl.x = fixX(tl.x, tl.y, -1);
  bl.x = fixX(bl.x, bl.y, -1);
  tr.x = fixX(tr.x, tr.y, 1);
  br.x = fixX(br.x, br.y, 1);
  const next = [tl, tr, br, bl];
  if (!isConvexQuad(next)) return corners;
  const scored = scoreDetection(next, null, w, h, null);
  if (!scored.ok) return corners;
  return clampCorners(next, w, h);
}

/**
 * Walk from the print core outward along a column to the sheet border.
 * Stops on: brightness drop (desk/keyboard), or a strong edge after leaving
 * the ink band — does NOT swallow the whole bright desk under the page.
 */
function sheetEdgeYAlongColumn(
  x,
  fromY,
  dir,
  gray,
  mag,
  w,
  h,
  paperFloor,
  edgeThr,
  maxStep
) {
  let last = fromY;
  let borderY = null;
  for (let s = 1; s <= maxStep; s += 1) {
    const y = Math.round(fromY + dir * s);
    if (y < 1 || y >= h - 1) break;
    const g = gray[y * w + x];
    const m = mag[y * w + x];
    if (g >= paperFloor - 8) {
      last = y;
      if (m >= edgeThr) borderY = y;
      continue;
    }
    // Left bright paper — prefer last strong edge, else last paper pixel.
    return borderY != null ? borderY : last;
  }
  return borderY != null ? borderY : last;
}

/**
 * Adjust top/bottom to the sheet border using ink as an anchor so we do not
 * expand into bright desk/legs, and do not stop on logo/table rules.
 * Keeps seed L/R x.
 */
function expandTopBottomToPaperBorder(corners, gray, mag, w, h, inkMask) {
  if (!corners || corners.length !== 4) return corners;
  const [tl0, tr0, br0, bl0] = corners;
  const paperFloor = Math.max(155, percentile(gray, 0.42));
  const edgeThr = Math.max(12, percentile(mag, 0.86));
  const seedH =
    (dist(tl0, bl0) + dist(tr0, br0)) * 0.5 || Math.max(1, h * 0.4);
  let inkSpan = inkVerticalSpan(inkMask, w, h);
  // Sparse ink-on-paper masks are common at 520px — fall back to dark-on-paper.
  if (!inkSpan) {
    const darkCut = Math.min(150, Math.max(85, percentile(gray, 0.22)));
    const loose = new Uint8Array(w * h);
    for (let y = 1; y < h - 1; y += 2) {
      for (let x = 1; x < w - 1; x += 2) {
        if (gray[y * w + x] > darkCut) continue;
        if (!inkSurroundedByPaper(gray, w, h, x, y, paperFloor)) continue;
        loose[y * w + x] = 1;
      }
    }
    inkSpan = inkVerticalSpan(loose, w, h);
  }
  // Prefer ink-band height over a shrunk seed so footer walks can reach the sheet edge.
  const inkH = inkSpan ? Math.max(1, inkSpan.bot - inkSpan.top) : seedH;
  const pageH = Math.max(seedH, inkH * 1.08);

  // Start walks from near the print core (ink), not from a possibly wrong seed.
  const coreTop = inkSpan
    ? inkSpan.top + Math.max(4, inkH * 0.08)
    : (tl0.y + tr0.y) * 0.5 + pageH * 0.08;
  const coreBot = inkSpan
    ? inkSpan.bot - Math.max(4, inkH * 0.08)
    : (bl0.y + br0.y) * 0.5 - pageH * 0.08;

  // How far blank margins may extend past ink (A4 headers/footers).
  const maxAboveInk = pageH * 0.22;
  const maxBelowInk = pageH * 0.14;
  const maxStepUp = Math.round(
    Math.min(pageH * 0.55, inkSpan ? coreTop - (inkSpan.top - maxAboveInk) : pageH * 0.45)
  );
  const maxStepDown = Math.round(
    Math.min(pageH * 0.45, inkSpan ? inkSpan.bot + maxBelowInk - coreBot : pageH * 0.35)
  );

  const xsTop = [
    Math.round(tl0.x),
    Math.round((tl0.x + tr0.x) / 2),
    Math.round(tr0.x),
  ];
  const xsBot = [
    Math.round(bl0.x),
    Math.round((bl0.x + br0.x) / 2),
    Math.round(br0.x),
  ];

  const topYs = [];
  const botYs = [];
  for (const x of xsTop) {
    const xx = Math.max(1, Math.min(w - 2, x));
    let y = sheetEdgeYAlongColumn(
      xx,
      coreTop,
      -1,
      gray,
      mag,
      w,
      h,
      paperFloor,
      edgeThr,
      Math.max(8, maxStepUp)
    );
    if (inkSpan) y = Math.max(y, inkSpan.top - maxAboveInk);
    if (inkSpan) y = Math.min(y, inkSpan.top + pageH * 0.05);
    topYs.push(y);
  }
  for (const x of xsBot) {
    const xx = Math.max(1, Math.min(w - 2, x));
    let y = sheetEdgeYAlongColumn(
      xx,
      coreBot,
      1,
      gray,
      mag,
      w,
      h,
      paperFloor,
      edgeThr,
      Math.max(8, maxStepDown)
    );
    if (inkSpan) y = Math.min(y, inkSpan.bot + maxBelowInk);
    if (inkSpan) y = Math.max(y, inkSpan.bot - pageH * 0.04);
    botYs.push(y);
  }

  // Tops may move up into blank margins. Bottoms: if the seed sits above the
  // ink band (shrunk to a table), allow a full walk down to ink+footer —
  // the old ±3.5% seed clamp made that undershoot unrecoverable.
  const seedTopL = tl0.y;
  const seedTopR = tr0.y;
  const seedBotL = bl0.y;
  const seedBotR = br0.y;
  let topYL = Math.min(topYs[0], seedTopL);
  let topYR = Math.min(topYs[2], seedTopR);
  if (inkSpan) {
    if (seedTopL > inkSpan.top + 4) {
      topYL = Math.min(topYL, Math.max(inkSpan.top - maxAboveInk, topYs[0]));
    }
    if (seedTopR > inkSpan.top + 4) {
      topYR = Math.min(topYR, Math.max(inkSpan.top - maxAboveInk, topYs[2]));
    }
  }
  let botYL = botYs[0];
  let botYR = botYs[2];
  if (inkSpan && (seedBotL < inkSpan.bot - 4 || seedBotR < inkSpan.bot - 4)) {
    const floor = inkSpan.bot + Math.max(2, pageH * 0.015);
    const ceil = inkSpan.bot + maxBelowInk;
    if (seedBotL < inkSpan.bot - 4) {
      botYL = Math.min(Math.max(botYL, floor), ceil);
    } else {
      botYL = Math.min(botYL, seedBotL + pageH * 0.04);
      botYL = Math.max(botYL, seedBotL - pageH * 0.08);
    }
    if (seedBotR < inkSpan.bot - 4) {
      botYR = Math.min(Math.max(botYR, floor), ceil);
    } else {
      botYR = Math.min(botYR, seedBotR + pageH * 0.04);
      botYR = Math.max(botYR, seedBotR - pageH * 0.08);
    }
  } else {
    botYL = Math.min(botYL, seedBotL + pageH * 0.04);
    botYR = Math.min(botYR, seedBotR + pageH * 0.04);
    botYL = Math.max(botYL, seedBotL - pageH * 0.1);
    botYR = Math.max(botYR, seedBotR - pageH * 0.1);
  }

  const nudgeY = (x, y, dir) => {
    let best = y;
    let bestM = mag[Math.round(y) * w + Math.round(x)] || 0;
    for (let d = 0; d <= 4; d += 1) {
      const yy = Math.round(y + dir * d);
      if (yy < 1 || yy >= h - 1) continue;
      const m = mag[yy * w + Math.round(x)];
      if (m >= bestM) {
        bestM = m;
        best = yy;
      }
    }
    return best;
  };

  const next = [
    { x: tl0.x, y: nudgeY(tl0.x, topYL, -1) },
    { x: tr0.x, y: nudgeY(tr0.x, topYR, -1) },
    { x: br0.x, y: nudgeY(br0.x, botYR, 1) },
    { x: bl0.x, y: nudgeY(bl0.x, botYL, 1) },
  ];
  if (!isConvexQuad(next)) return corners;
  const scored = scoreDetection(next, null, w, h, null);
  if (!scored.ok) return corners;
  return clampCorners(next, w, h);
}

function dilateMask(mask, w, h, radius) {
  const r = Math.max(1, radius | 0);
  const out = new Uint8Array(w * h);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      let on = 0;
      for (let dy = -r; dy <= r && !on; dy += 1) {
        for (let dx = -r; dx <= r; dx += 1) {
          const xx = x + dx;
          const yy = y + dy;
          if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
          if (mask[yy * w + xx]) {
            on = 1;
            break;
          }
        }
      }
      out[y * w + x] = on;
    }
  }
  return out;
}

/**
 * Page corners from edges in a halo just outside the ink (print) core.
 * Ignores internal table lines and bright desk far from the page.
 */
function detectViaInkHaloCorners(gray, mag, inkMask, w, h) {
  const inkN = inkMask.reduce((s, v) => s + v, 0);
  if (inkN < 80) return null;
  const span = inkVerticalSpan(inkMask, w, h);
  if (!span) return null;

  // Halo: outside ink, inside a modest dilation — the page margin ring.
  const inner = dilateMask(inkMask, w, h, 2);
  const pad = Math.max(10, Math.round(Math.min(w, h) * 0.07));
  const outer = dilateMask(inkMask, w, h, pad);
  const edgeThr = Math.max(14, percentile(mag, 0.88));
  const paperFloor = Math.max(150, percentile(gray, 0.4));

  let sx = 0;
  let sy = 0;
  let nInk = 0;
  for (let y = 0; y < h; y += 2) {
    for (let x = 0; x < w; x += 2) {
      if (!inkMask[y * w + x]) continue;
      sx += x;
      sy += y;
      nInk += 1;
    }
  }
  if (nInk < 20) return null;
  const cx = sx / nInk;
  const cy = sy / nInk;

  const sides = [[], [], [], []]; // top, right, bottom, left
  for (let y = 1; y < h - 1; y += 1) {
    for (let x = 1; x < w - 1; x += 1) {
      const i = y * w + x;
      if (!outer[i] || inner[i]) continue;
      if (mag[i] < edgeThr) continue;
      // Prefer bright-ish border (paper) — skip dark desk/legs edges.
      if (gray[i] < paperFloor - 40) continue;
      const ang = (Math.atan2(y - cy, x - cx) * 180) / Math.PI;
      let side;
      if (ang >= -135 && ang < -45) side = 0; // top
      else if (ang >= -45 && ang < 45) side = 1; // right
      else if (ang >= 45 && ang < 135) side = 2; // bottom
      else side = 3; // left
      sides[side].push({ x, y });
    }
  }
  if (sides.some((s) => s.length < 12)) return null;

  function fitLine(pts) {
    if (pts.length < 12) return null;
    let bestIn = 0;
    let best = null;
    const step = Math.max(1, Math.floor(pts.length / 20));
    for (let t = 0; t < 40; t += 1) {
      const i1 = (t * step) % pts.length;
      const i2 = (i1 + Math.max(5, Math.floor(pts.length / 4))) % pts.length;
      const p1 = pts[i1];
      const p2 = pts[i2];
      let a = p1.y - p2.y;
      let b = p2.x - p1.x;
      let c = -(a * p1.x + b * p1.y);
      const nrm = Math.hypot(a, b);
      if (nrm < 1e-6) continue;
      a /= nrm;
      b /= nrm;
      c /= nrm;
      let inn = 0;
      let sx2 = 0;
      let sy2 = 0;
      let nn = 0;
      for (const p of pts) {
        if (Math.abs(a * p.x + b * p.y + c) < 2.5) {
          inn += 1;
          sx2 += p.x;
          sy2 += p.y;
          nn += 1;
        }
      }
      if (inn <= bestIn || nn < 8) continue;
      bestIn = inn;
      const mx = sx2 / nn;
      const my = sy2 / nn;
      let sxx = 0;
      let sxy = 0;
      let syy = 0;
      for (const p of pts) {
        if (Math.abs(a * p.x + b * p.y + c) >= 2.5) continue;
        const dx = p.x - mx;
        const dy = p.y - my;
        sxx += dx * dx;
        sxy += dx * dy;
        syy += dy * dy;
      }
      const ang = 0.5 * Math.atan2(2 * sxy, sxx - syy);
      const ux = Math.cos(ang);
      const uy = Math.sin(ang);
      let na = -uy;
      let nb = ux;
      let nc = -(na * mx + nb * my);
      const nn2 = Math.hypot(na, nb) || 1;
      best = { a: na / nn2, b: nb / nn2, c: nc / nn2 };
    }
    return best;
  }

  function intersect(L1, L2) {
    if (!L1 || !L2) return null;
    const det = L1.a * L2.b - L2.a * L1.b;
    if (Math.abs(det) < 1e-8) return null;
    return {
      x: (L2.b * L1.c - L1.b * L2.c) / det,
      y: (L1.a * L2.c - L2.a * L1.c) / det,
    };
  }

  const lines = sides.map(fitLine);
  if (lines.filter(Boolean).length < 4) return null;
  const tl = intersect(lines[3], lines[0]);
  const tr = intersect(lines[0], lines[1]);
  const br = intersect(lines[1], lines[2]);
  const bl = intersect(lines[2], lines[3]);
  if (!tl || !tr || !br || !bl) return null;
  let corners = clampCorners([tl, tr, br, bl], w, h);
  if (!isConvexQuad(corners)) return null;
  // Halo result should cover most ink.
  let inkIn = 0;
  let inkAll = 0;
  const step = Math.max(1, Math.floor(Math.min(w, h) / 60));
  for (let y = 0; y < h; y += step) {
    for (let x = 0; x < w; x += step) {
      if (!inkMask[y * w + x]) continue;
      inkAll += 1;
      if (pointInConvexQuad(x, y, corners)) inkIn += 1;
    }
  }
  if (inkAll > 0 && inkIn / inkAll < 0.72) return null;
  const scored = scoreDetection(corners, null, w, h, inkN);
  if (!scored.ok) return null;
  return corners;
}

/**
 * Local search: nudge each corner to maximize edge energy along its two sides.
 * Search window is small so we lock onto the sheet border near the seed,
 * not distant desk/keyboard edges.
 */
function refineCornersMaximizeEdgeEnergy(corners, mag, w, h) {
  if (!corners || corners.length !== 4) return corners;
  const pageW =
    (dist(corners[0], corners[1]) + dist(corners[3], corners[2])) * 0.5 ||
    w * 0.4;
  const pageH =
    (dist(corners[0], corners[3]) + dist(corners[1], corners[2])) * 0.5 ||
    h * 0.4;
  const radX = Math.max(4, Math.round(pageW * 0.045));
  const radY = Math.max(4, Math.round(pageH * 0.045));

  function edgeEnergy(a, b) {
    const n = Math.max(8, Math.round(dist(a, b) / 2));
    let sum = 0;
    let cnt = 0;
    for (let i = 0; i <= n; i += 1) {
      const t = i / n;
      const x = Math.round(a.x + (b.x - a.x) * t);
      const y = Math.round(a.y + (b.y - a.y) * t);
      if (x < 1 || y < 1 || x >= w - 1 || y >= h - 1) continue;
      sum += mag[y * w + x];
      cnt += 1;
    }
    return cnt ? sum / cnt : 0;
  }

  let cur = corners.map((p) => ({ x: p.x, y: p.y }));
  for (let pass = 0; pass < 3; pass += 1) {
    const next = cur.map((p) => ({ x: p.x, y: p.y }));
    for (let i = 0; i < 4; i += 1) {
      const prev = cur[(i + 3) % 4];
      const fol = cur[(i + 1) % 4];
      let best = cur[i];
      let bestE = edgeEnergy(prev, cur[i]) + edgeEnergy(cur[i], fol);
      for (let dy = -radY; dy <= radY; dy += 2) {
        for (let dx = -radX; dx <= radX; dx += 2) {
          const cand = {
            x: Math.min(w - 2, Math.max(1, cur[i].x + dx)),
            y: Math.min(h - 2, Math.max(1, cur[i].y + dy)),
          };
          const e = edgeEnergy(prev, cand) + edgeEnergy(cand, fol);
          if (e > bestE) {
            bestE = e;
            best = cand;
          }
        }
      }
      next[i] = best;
    }
    if (!isConvexQuad(next)) break;
    cur = next;
  }
  return cur;
}

/**
 * Whiteness cue from RGB (paper ≈ high luma + low chroma; beige desk is warmer).
 */
function whitenessMap(rgba, w, h) {
  const out = new Float32Array(w * h);
  for (let i = 0, p = 0; i < rgba.length; i += 4, p += 1) {
    const r = rgba[i];
    const g = rgba[i + 1];
    const b = rgba[i + 2];
    const luma = 0.299 * r + 0.587 * g + 0.114 * b;
    const chroma = Math.max(r, g, b) - Math.min(r, g, b);
    // High = paper-like; beige desk loses score via chroma / lower blue.
    out[p] = luma - chroma * 0.65 - Math.max(0, r - b) * 0.15;
  }
  return out;
}

/**
 * Background prototypes from the four image-corner patches only.
 * A full border strip often overlaps the sheet on large in-frame pages.
 */
function borderPrototypes(gray, white, w, h) {
  const pw = Math.max(4, Math.floor(w * 0.08));
  const ph = Math.max(4, Math.floor(h * 0.08));
  const gS = [];
  const wS = [];
  const patches = [
    [0, 0, pw, ph],
    [w - pw, 0, w, ph],
    [0, h - ph, pw, h],
    [w - pw, h - ph, w, h],
  ];
  for (const [x0, y0, x1, y1] of patches) {
    for (let y = y0; y < y1; y += 2) {
      for (let x = x0; x < x1; x += 2) {
        gS.push(gray[y * w + x]);
        wS.push(white[y * w + x]);
      }
    }
  }
  if (gS.length < 16) {
    return {
      bgG: percentile(gray, 0.3),
      bgW: percentile(white, 0.3),
      bgGDev: 12,
      bgWDev: 10,
    };
  }
  // Prefer the darker / less-white side of corner samples (desk under sheet).
  const bgG = percentile(gS, 0.4);
  const bgW = percentile(wS, 0.35);
  const bgGDev = Math.max(3, percentile(gS.map((v) => Math.abs(v - bgG)), 0.55));
  const bgWDev = Math.max(3, percentile(wS.map((v) => Math.abs(v - bgW)), 0.55));
  return { bgG, bgW, bgGDev, bgWDev };
}

/**
 * Print seed at downscaled sizes. Strict ink is sparse, so also take dark
 * pixels on paper — but NOT bright textured paper (that floods blank sheets).
 */
function buildContentSeed(gray, variance, inkMask, w, h) {
  void variance;
  const darkCut = Math.min(150, Math.max(85, percentile(gray, 0.22)));
  const paperFloor = Math.max(darkCut + 25, percentile(gray, 0.5));
  const seed = new Uint8Array(w * h);
  let n = 0;
  let minX = w;
  let minY = h;
  let maxX = 0;
  let maxY = 0;
  let sx = 0;
  let sy = 0;
  for (let y = 1; y < h - 1; y += 1) {
    for (let x = 1; x < w - 1; x += 1) {
      const i = y * w + x;
      let on = inkMask[i] ? 1 : 0;
      if (
        !on &&
        gray[i] <= darkCut &&
        inkSurroundedByPaper(gray, w, h, x, y, paperFloor)
      ) {
        on = 1;
      }
      if (!on) continue;
      seed[i] = 1;
      n += 1;
      sx += x;
      sy += y;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  // If still tiny, relax dark cut slightly (keep dark-only — no bright texture).
  if (n < 80) {
    const loose = Math.min(160, darkCut + 18);
    for (let y = 1; y < h - 1; y += 1) {
      for (let x = 1; x < w - 1; x += 1) {
        const i = y * w + x;
        if (seed[i]) continue;
        if (gray[i] > loose) continue;
        if (!inkSurroundedByPaper(gray, w, h, x, y, paperFloor)) continue;
        seed[i] = 1;
        n += 1;
        sx += x;
        sy += y;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (n < 40) return null;
  return {
    seed,
    n,
    cx: sx / n,
    cy: sy / n,
    box: { minX, minY, maxX, maxY },
    paperFloor,
  };
}

/**
 * Paper prototypes from blank sheet near print content (not the desk border).
 */
function paperPrototypes(gray, white, content, w, h) {
  const { box, cx, cy, seed } = content;
  const pad = Math.max(8, Math.round(Math.min(w, h) * 0.06));
  const x0 = Math.max(0, box.minX - pad);
  const y0 = Math.max(0, box.minY - pad);
  const x1 = Math.min(w - 1, box.maxX + pad);
  const y1 = Math.min(h - 1, box.maxY + pad);
  const gS = [];
  const wS = [];
  for (let y = y0; y <= y1; y += 2) {
    for (let x = x0; x <= x1; x += 2) {
      const i = y * w + x;
      if (seed[i]) continue;
      // Blank paper: bright + relatively white vs local print core.
      if (gray[i] < 145) continue;
      if (white[i] < 110) continue;
      gS.push(gray[i]);
      wS.push(white[i]);
    }
  }
  if (gS.length < 24) {
    const r = Math.max(20, Math.round(Math.min(w, h) * 0.12));
    const ccx = Math.round(cx);
    const ccy = Math.round(cy);
    for (let y = Math.max(0, ccy - r); y <= Math.min(h - 1, ccy + r); y += 2) {
      for (let x = Math.max(0, ccx - r); x <= Math.min(w - 1, ccx + r); x += 2) {
        const i = y * w + x;
        if (seed[i]) continue;
        if (gray[i] < 140) continue;
        gS.push(gray[i]);
        wS.push(white[i]);
      }
    }
  }
  if (gS.length < 12) return null;
  return {
    paperG: percentile(gS, 0.55),
    paperW: percentile(wS, 0.55),
  };
}

/**
 * Soft paper vs background score map. Positive ⇒ more paper-like.
 * Uses prototypes + content dilation prior so blank margins stay "paper"
 * even when the desk is almost as bright (the common failure mode).
 */
function buildPaperScoreMap(gray, white, contentSeed, w, h, proto, border) {
  const { paperG, paperW } = proto;
  const { bgG, bgW, bgGDev, bgWDev } = border;
  const gSep = Math.max(4, Math.abs(paperG - bgG));
  const wSep = Math.max(4, Math.abs(paperW - bgW));
  // Margin ring past print — fixed px so dense seeds cannot flood the frame.
  const nearR = Math.max(6, Math.min(14, Math.round(Math.min(w, h) * 0.035)));
  const farR = Math.max(12, Math.min(28, Math.round(Math.min(w, h) * 0.07)));
  const nearInk = dilateMask(contentSeed, w, h, nearR);
  const farInk = dilateMask(contentSeed, w, h, farR);
  const lowContrast = gSep < 12 && wSep < 14;
  const midG = (paperG + bgG) * 0.5;
  const midW = (paperW + bgW) * 0.5;
  const score = new Float32Array(w * h);
  for (let i = 0; i < w * h; i += 1) {
    if (contentSeed[i]) {
      score[i] = 40;
      continue;
    }
    const g = gray[i];
    const wh = white[i];
    const dPaper =
      Math.abs(g - paperG) / gSep + Math.abs(wh - paperW) / wSep;
    const dBg = Math.abs(g - bgG) / gSep + Math.abs(wh - bgW) / wSep;
    let s = dBg - dPaper; // >0 closer to paper than bg
    // Hard mid-point: with good separation (paper~200, desk~150) this is decisive.
    if (g >= midG + 4) s += 1.1;
    else if (g <= midG - 4) s -= 1.1;
    if (wh >= midW + 4) s += 0.9;
    else if (wh <= midW - 4) s -= 0.9;
    // Whiteness excess over border is a strong sheet cue on beige desks.
    s += ((wh - bgW) / Math.max(6, bgWDev)) * (lowContrast ? 0.9 : 0.35);
    s += ((g - bgG) / Math.max(6, bgGDev)) * (lowContrast ? 0.15 : 0.15);
    if (lowContrast) {
      s += Math.max(0, 1.2 - Math.abs(wh - paperW) / 8) * 0.7;
      s += Math.max(0, 1.0 - Math.abs(g - paperG) / 10) * 0.35;
    }
    // Dark floor / legs: strong anti-paper.
    if (g < bgG - bgGDev * 1.8 && g < 130) s -= 3;
    if (nearInk[i]) s += lowContrast ? 1.0 : 0.45;
    else if (farInk[i]) s += lowContrast ? 0.35 : 0.15;
    // Far from print: require clearer paper signal (avoid eating the desk).
    if (!farInk[i] && s < 1.0) s -= 0.85;
    score[i] = s;
  }
  return score;
}

/**
 * Dilate only into paper-like pixels (score + brightness), never into desk.
 */
function morphClosePaperScore(mask, score, gray, w, h, radius, brightMin, scoreMin) {
  const r = Math.max(1, radius | 0);
  const allow = (i) => gray[i] >= brightMin && score[i] >= scoreMin;
  const dilate = (src) => {
    const dst = new Uint8Array(w * h);
    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < w; x += 1) {
        const i = y * w + x;
        if (!allow(i) && !src[i]) continue;
        let ok = src[i] ? 1 : 0;
        if (!ok) {
          for (let dy = -r; dy <= r && !ok; dy += 1) {
            for (let dx = -r; dx <= r; dx += 1) {
              const xx = x + dx;
              const yy = y + dy;
              if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
              if (src[yy * w + xx]) {
                ok = 1;
                break;
              }
            }
          }
        }
        // New pixels must still look like paper.
        if (ok && (src[i] || allow(i))) dst[i] = 1;
      }
    }
    return dst;
  };
  const erode = (src) => {
    const dst = new Uint8Array(w * h);
    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < w; x += 1) {
        let ok = 1;
        for (let dy = -r; dy <= r && ok; dy += 1) {
          for (let dx = -r; dx <= r; dx += 1) {
            const xx = x + dx;
            const yy = y + dy;
            if (xx < 0 || yy < 0 || xx >= w || yy >= h || !src[yy * w + xx]) {
              ok = 0;
              break;
            }
          }
        }
        dst[y * w + x] = ok;
      }
    }
    return dst;
  };
  return erode(dilate(dilate(mask)));
}

/**
 * Grow print seed into bright blank margins (paper), blocked from desk by score.
 * Then keep the component that contains the content centroid.
 */
function paperMaskFromScore(score, contentSeed, gray, w, h, cx, cy, paperFloor) {
  const thr = 0.45;
  const raw = new Uint8Array(w * h);
  for (let i = 0; i < score.length; i += 1) {
    raw[i] = score[i] > thr || contentSeed[i] ? 1 : 0;
  }
  const brightMin = Math.max(paperFloor - 4, percentile(gray, 0.48));
  // Grow into blank sheet only where paper-score agrees (beige desk stays out).
  let grown = morphClosePaperScore(raw, score, gray, w, h, 2, brightMin, 0.25);
  grown = morphClosePaperScore(grown, score, gray, w, h, 1, brightMin, 0.15);
  for (let i = 0; i < grown.length; i += 1) {
    if (contentSeed[i]) grown[i] = 1;
  }

  // Flood-fill from content centroid; if seed off mask, search nearby.
  const seedX = Math.max(0, Math.min(w - 1, Math.round(cx)));
  const seedY = Math.max(0, Math.min(h - 1, Math.round(cy)));
  let sx = seedX;
  let sy = seedY;
  if (!grown[sy * w + sx]) {
    let found = false;
    for (let r = 1; r < 40 && !found; r += 1) {
      for (let dy = -r; dy <= r && !found; dy += 1) {
        for (let dx = -r; dx <= r; dx += 1) {
          const x = seedX + dx;
          const y = seedY + dy;
          if (x < 0 || y < 0 || x >= w || y >= h) continue;
          if (grown[y * w + x]) {
            sx = x;
            sy = y;
            found = true;
            break;
          }
        }
      }
    }
    if (!found) return grown;
  }

  const out = new Uint8Array(w * h);
  const stack = [sy * w + sx];
  out[sy * w + sx] = 1;
  while (stack.length) {
    const i = stack.pop();
    const x = i % w;
    const y = (i / w) | 0;
    for (const [dx, dy] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ]) {
      const xx = x + dx;
      const yy = y + dy;
      if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
      const j = yy * w + xx;
      if (out[j] || !grown[j]) continue;
      out[j] = 1;
      stack.push(j);
    }
  }
  let n = 0;
  let seedN = 0;
  for (let i = 0; i < out.length; i += 1) {
    n += out[i];
    seedN += contentSeed[i];
  }
  if (n < seedN * 0.5) return grown;
  return out;
}

/** Push a quad outward so it fully contains the content bounding box. */
function expandCornersToBox(corners, box, w, h) {
  if (!corners || corners.length !== 4) return corners;
  const pad = Math.max(2, Math.round(Math.min(w, h) * 0.01));
  const x0 = box.minX - pad;
  const y0 = box.minY - pad;
  const x1 = box.maxX + pad;
  const y1 = box.maxY + pad;
  const next = corners.map((p) => ({ x: p.x, y: p.y }));
  // TL, TR, BR, BL — clamp each toward covering the box extremes.
  next[0] = { x: Math.min(next[0].x, x0), y: Math.min(next[0].y, y0) };
  next[1] = { x: Math.max(next[1].x, x1), y: Math.min(next[1].y, y0) };
  next[2] = { x: Math.max(next[2].x, x1), y: Math.max(next[2].y, y1) };
  next[3] = { x: Math.min(next[3].x, x0), y: Math.max(next[3].y, y1) };
  if (!isConvexQuad(next)) return corners;
  return clampCorners(next, w, h);
}

/**
 * Outermost paper→background hit along a ray (mask-based, not print edges).
 * When luma separation is clear, also stop where gray falls through midG.
 */
function rayPaperBorderHit(mask, score, cx, cy, dx, dy, w, h, gray, midG) {
  const maxR = Math.hypot(w, h);
  let lastPaper = null;
  let seen = false;
  const useLuma = gray && midG != null && Number.isFinite(midG);
  for (let r = 2; r < maxR; r += 1) {
    const x = Math.round(cx + dx * r);
    const y = Math.round(cy + dy * r);
    if (x < 1 || y < 1 || x >= w - 1 || y >= h - 1) {
      return lastPaper;
    }
    const i = y * w + x;
    const lumaPaper = !useLuma || gray[i] >= midG - 2;
    const on = (mask[i] || score[i] > 0.2) && lumaPaper;
    if (on) {
      seen = true;
      lastPaper = { x, y };
      continue;
    }
    if (!seen) continue;
    // Left paper: confirm a short run of background ahead.
    let bgRun = 0;
    for (let k = 1; k <= 4; k += 1) {
      const xx = Math.round(cx + dx * (r + k));
      const yy = Math.round(cy + dy * (r + k));
      if (xx < 0 || yy < 0 || xx >= w || yy >= h) {
        bgRun += 1;
        continue;
      }
      const j = yy * w + xx;
      const dark = useLuma && gray[j] < midG - 4;
      if ((!mask[j] && score[j] <= 0.15) || dark) bgRun += 1;
    }
    if (bgRun >= 3) return lastPaper || { x, y };
  }
  return lastPaper;
}

/**
 * Fit side lines to paper-mask boundary points (not sobel / table rules).
 */
function refineCornersByPaperMask(corners, mask, w, h) {
  if (!corners || corners.length !== 4) return corners;
  const band = Math.max(4, Math.round(Math.min(w, h) * 0.035));
  const cx0 = corners.reduce((s, p) => s + p.x, 0) / 4;
  const cy0 = corners.reduce((s, p) => s + p.y, 0) / 4;

  function boundaryPtsAlong(a, b) {
    const pts = [];
    const n = Math.max(10, Math.round(dist(a, b) / 2));
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    let nx = -dy / len;
    let ny = dx / len;
    const midX = (a.x + b.x) * 0.5;
    const midY = (a.y + b.y) * 0.5;
    if (nx * (midX - cx0) + ny * (midY - cy0) < 0) {
      nx = -nx;
      ny = -ny;
    }
    for (let i = 0; i <= n; i += 1) {
      const t = i / n;
      const px = a.x + dx * t;
      const py = a.y + dy * t;
      let best = null;
      // Prefer outermost paper pixel along the normal.
      for (let s = -Math.floor(band * 0.4); s <= band; s += 1) {
        const x = Math.round(px + nx * s);
        const y = Math.round(py + ny * s);
        if (x < 1 || y < 1 || x >= w - 1 || y >= h - 1) continue;
        if (!mask[y * w + x]) continue;
        // Edge if any outward neighbor is non-paper.
        const ox = Math.round(x + nx);
        const oy = Math.round(y + ny);
        if (ox < 0 || oy < 0 || ox >= w || oy >= h || !mask[oy * w + ox]) {
          best = { x, y };
        }
      }
      if (best) pts.push(best);
    }
    return pts;
  }

  function fitLine(pts) {
    if (pts.length < 8) return null;
    let bestIn = 0;
    let best = null;
    const step = Math.max(1, Math.floor(pts.length / 24));
    for (let trial = 0; trial < 48; trial += 1) {
      const i1 = (trial * step) % pts.length;
      const i2 = (i1 + Math.max(3, Math.floor(pts.length / 5))) % pts.length;
      const p1 = pts[i1];
      const p2 = pts[i2];
      let a = p1.y - p2.y;
      let b = p2.x - p1.x;
      let c = -(a * p1.x + b * p1.y);
      const nrm = Math.hypot(a, b);
      if (nrm < 1e-6) continue;
      a /= nrm;
      b /= nrm;
      c /= nrm;
      let inn = 0;
      for (const p of pts) {
        if (Math.abs(a * p.x + b * p.y + c) < 2.4) inn += 1;
      }
      if (inn > bestIn) {
        bestIn = inn;
        let sx = 0;
        let sy = 0;
        let n = 0;
        for (const p of pts) {
          if (Math.abs(a * p.x + b * p.y + c) >= 2.4) continue;
          sx += p.x;
          sy += p.y;
          n += 1;
        }
        if (n < 4) continue;
        const mx = sx / n;
        const my = sy / n;
        let sxx = 0;
        let sxy = 0;
        let syy = 0;
        for (const p of pts) {
          if (Math.abs(a * p.x + b * p.y + c) >= 2.4) continue;
          const dx = p.x - mx;
          const dy = p.y - my;
          sxx += dx * dx;
          sxy += dx * dy;
          syy += dy * dy;
        }
        const ang = 0.5 * Math.atan2(2 * sxy, sxx - syy);
        const ux = Math.cos(ang);
        const uy = Math.sin(ang);
        let na = -uy;
        let nb = ux;
        let nc = -(na * mx + nb * my);
        const nn = Math.hypot(na, nb) || 1;
        best = { a: na / nn, b: nb / nn, c: nc / nn };
      }
    }
    return best;
  }

  function intersect(L1, L2) {
    if (!L1 || !L2) return null;
    const det = L1.a * L2.b - L2.a * L1.b;
    if (Math.abs(det) < 1e-8) return null;
    return {
      x: (L2.b * L1.c - L1.b * L2.c) / det,
      y: (L1.a * L2.c - L2.a * L1.c) / det,
    };
  }

  const [tl, tr, br, bl] = corners;
  const lines = [
    fitLine(boundaryPtsAlong(tl, tr)),
    fitLine(boundaryPtsAlong(tr, br)),
    fitLine(boundaryPtsAlong(br, bl)),
    fitLine(boundaryPtsAlong(bl, tl)),
  ];
  if (lines.some((L) => !L)) return corners;
  const next = [
    intersect(lines[3], lines[0]),
    intersect(lines[0], lines[1]),
    intersect(lines[1], lines[2]),
    intersect(lines[2], lines[3]),
  ];
  if (next.some((p) => !p || !Number.isFinite(p.x) || !Number.isFinite(p.y))) {
    return corners;
  }
  const maxMove = Math.hypot(w, h) * 0.1;
  for (let i = 0; i < 4; i += 1) {
    if (dist(next[i], corners[i]) > maxMove) return corners;
  }
  if (!isConvexQuad(next)) return corners;
  return clampCorners(next, w, h);
}

/**
 * Primary detector: sheet↔background via paper/bg prototypes + mask outline.
 * Does not lock onto internal table/print edges (those are sobel-strong).
 */
function detectViaPaperBackgroundEdge(rgba, gray, mag, inkMask, w, h, variance) {
  const varMap = variance || localVariance(gray, w, h, 2);
  const content = buildContentSeed(gray, varMap, inkMask, w, h);
  if (!content) {
    return null;
  }

  const white = whitenessMap(rgba, w, h);
  const whiteBlur = boxBlur(white, w, h, 1);
  const border = borderPrototypes(gray, whiteBlur, w, h);
  const proto = paperPrototypes(gray, whiteBlur, content, w, h);
  if (!proto) {
    return null;
  }

  const score = buildPaperScoreMap(
    gray,
    whiteBlur,
    content.seed,
    w,
    h,
    proto,
    border
  );
  let mask = paperMaskFromScore(
    score,
    content.seed,
    gray,
    w,
    h,
    content.cx,
    content.cy,
    content.paperFloor
  );
  // Hard spatial prior: blank margins exist, but not half the frame.
  // Clip mask to content-box + generous pad so desk beyond the sheet is out.
  {
    const bw = content.box.maxX - content.box.minX + 1;
    const bh = content.box.maxY - content.box.minY + 1;
    const padX = Math.max(12, Math.round(bw * 0.28));
    const padY = Math.max(14, Math.round(bh * 0.26));
    const x0 = Math.max(0, content.box.minX - padX);
    const y0 = Math.max(0, content.box.minY - padY);
    const x1 = Math.min(w - 1, content.box.maxX + padX);
    const y1 = Math.min(h - 1, content.box.maxY + padY);
    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < w; x += 1) {
        if (x < x0 || x > x1 || y < y0 || y > y1) mask[y * w + x] = 0;
      }
    }
    // Re-attach content seed inside clip.
    for (let i = 0; i < mask.length; i += 1) {
      if (content.seed[i]) mask[i] = 1;
    }
  }

  let maskN = 0;
  for (let i = 0; i < mask.length; i += 1) {
    maskN += mask[i];
  }
  // Mask must extend past print into blank margins; if not, still try score rays.
  const useMask = maskN >= content.n * 1.05;

  const midG = (proto.paperG + border.bgG) * 0.5;
  const gSep = Math.abs(proto.paperG - border.bgG);
  const lumaGate = gSep >= 18 ? midG : null;

  const hits = [];
  for (let a = 0; a < 360; a += 1.25) {
    const rad = (a * Math.PI) / 180;
    const dx = Math.cos(rad);
    const dy = Math.sin(rad);
    let hit = null;
    if (useMask) {
      hit = rayPaperBorderHit(
        mask,
        score,
        content.cx,
        content.cy,
        dx,
        dy,
        w,
        h,
        gray,
        lumaGate
      );
    }
    if (!hit) {
      hit = rayScoreBorderHit(
        score,
        content.cx,
        content.cy,
        dx,
        dy,
        w,
        h,
        gray,
        lumaGate
      );
    }
    if (hit) hits.push(hit);
  }
  // Silhouette only when mask is sheet-sized (not desk-swollen / not tiny).
  const maskFrac = maskN / (w * h);
  if (useMask && maskFrac > 0.18 && maskFrac < 0.68) {
    for (let y = 1; y < h - 1; y += 2) {
      for (let x = 1; x < w - 1; x += 2) {
        const i = y * w + x;
        if (!mask[i]) continue;
        if (
          !mask[i - 1] ||
          !mask[i + 1] ||
          !mask[i - w] ||
          !mask[i + w]
        ) {
          hits.push({ x, y });
        }
      }
    }
  }
  if (hits.length < 40) {
    return null;
  }

  const hull = convexHull(hits);
  if (hull.length < 4) {
    return null;
  }
  let corners = hullToFourCorners(hull, w, h);
  if (!corners) {
    return null;
  }
  corners = clampCorners(corners, w, h);
  if (!isConvexQuad(corners)) {
    return null;
  }

  if (useMask) {
    corners = refineCornersByPaperMask(corners, mask, w, h);
    corners = clampCorners(corners, w, h);
    if (!isConvexQuad(corners)) {
      return null;
    }
  }

  // Ensure print content is inside the quad (rays often stop at the ink edge).
  corners = expandCornersToBox(corners, content.box, w, h);

  // Capture most print content — reject inner table-box shrinks.
  let seedIn = 0;
  let seedAll = 0;
  const step = Math.max(1, Math.floor(Math.min(w, h) / 70));
  for (let y = 0; y < h; y += step) {
    for (let x = 0; x < w; x += step) {
      if (!content.seed[y * w + x]) continue;
      seedAll += 1;
      if (pointInConvexQuad(x, y, corners)) seedIn += 1;
    }
  }
  if (seedAll > 8 && seedIn / seedAll < 0.75) {
    return null;
  }

  // Prefer covering the paper mask when it is trustworthy (not desk-swollen).
  if (useMask && maskN < w * h * 0.72) {
    let paperIn = 0;
    let paperAll = 0;
    for (let y = 0; y < h; y += step) {
      for (let x = 0; x < w; x += step) {
        if (!mask[y * w + x]) continue;
        paperAll += 1;
        if (pointInConvexQuad(x, y, corners)) paperIn += 1;
      }
    }
    if (paperAll > 0 && paperIn / paperAll < 0.6) {
      return null;
    }
  }

  // Reject tiny quads relative to content bbox (classic table lock-in).
  const { box } = content;
  const inkW = box.maxX - box.minX + 1;
  const inkH = box.maxY - box.minY + 1;
  const quadW =
    (dist(corners[0], corners[1]) + dist(corners[3], corners[2])) * 0.5;
  const quadH =
    (dist(corners[0], corners[3]) + dist(corners[1], corners[2])) * 0.5;
  if (quadW < inkW * 0.92 || quadH < inkH * 0.92) {
    return null;
  }

  // Pass mask area (not sparse ink count) so blob_small does not false-reject.
  let scored = scoreDetection(corners, null, w, h, Math.max(maskN, content.n));
  // If we overshot into the desk, pull corners inward toward content box once.
  if (!scored.ok && (scored.reason === "frame_hug" || scored.reason === "area")) {
    const box = content.box;
    const cxBox = (box.minX + box.maxX) / 2;
    const cyBox = (box.minY + box.maxY) / 2;
    const shrunk = corners.map((p) => ({
      x: p.x * 0.72 + cxBox * 0.28,
      y: p.y * 0.72 + cyBox * 0.28,
    }));
    const fixed = expandCornersToBox(clampCorners(shrunk, w, h), box, w, h);
    if (isConvexQuad(fixed)) {
      corners = fixed;
      scored = scoreDetection(corners, null, w, h, Math.max(maskN, content.n));
    }
  }
  if (!scored.ok) {
    return null;
  }
  void mag;
  return corners;
}


/**
 * Outermost paper→bg transition using soft score (+ luma mid-point when clear).
 * Uses a per-ray on-page baseline so low-contrast desks still show a drop.
 */
function rayScoreBorderHit(score, cx, cy, dx, dy, w, h, gray, midG) {
  const maxR = Math.hypot(w, h);
  const useLuma = gray && midG != null && Number.isFinite(midG);
  // Sample on-page baseline (inner third of ray).
  const base = [];
  for (let r = 4; r < maxR * 0.35; r += 2) {
    const x = Math.round(cx + dx * r);
    const y = Math.round(cy + dy * r);
    if (x < 1 || y < 1 || x >= w - 1 || y >= h - 1) break;
    base.push(score[y * w + x]);
  }
  if (base.length < 4) return null;
  base.sort((a, b) => a - b);
  const baseMed = base[Math.floor(base.length * 0.5)];
  const dropThr = Math.min(0.15, baseMed - 0.35);

  let lastPaper = null;
  let seen = false;
  for (let r = 2; r < maxR; r += 1) {
    const x = Math.round(cx + dx * r);
    const y = Math.round(cy + dy * r);
    if (x < 1 || y < 1 || x >= w - 1 || y >= h - 1) return lastPaper;
    const s = score[y * w + x];
    const g = useLuma ? gray[y * w + x] : 255;
    if (s > dropThr + 0.2 && (!useLuma || g >= midG - 2)) {
      seen = true;
      lastPaper = { x, y };
      continue;
    }
    if (!seen) continue;
    let bgRun = 0;
    for (let k = 0; k < 5; k += 1) {
      const xx = Math.round(cx + dx * (r + k));
      const yy = Math.round(cy + dy * (r + k));
      if (xx < 0 || yy < 0 || xx >= w || yy >= h) {
        bgRun += 1;
        continue;
      }
      const ss = score[yy * w + xx];
      const gg = useLuma ? gray[yy * w + xx] : 0;
      if (ss < dropThr || (useLuma && gg < midG - 4)) bgRun += 1;
    }
    if (bgRun >= 3) return lastPaper;
  }
  return lastPaper;
}

/* ===========================================================================
 * [Fable 5.1 · Step-edge sheet detector]  (v13/v14 primary path)
 * ---------------------------------------------------------------------------
 * Agreed with the product owner after measuring the 23-photo calibration set
 * (`fixtures/corners/labels.scaled.json`). Everything in this block is a
 * reference implementation; change it only with an explicit override.
 *
 * WHY THE OLD APPROACHES PLATEAUED (measured, not assumed):
 *   At the sheet edge the desk luma equals the paper luma (ring 190–200 vs
 *   paper ≈197). Warmth (R−B) separates paper from desk in ~17/23 photos but
 *   inverts in 3 and is ~0 in 4. Region/mask growing, sobel snapping and
 *   luma rays therefore all drifted onto table rules or the frame (~5.5%
 *   mean corner error). This detector gets ≈1.0% mean, 21/23 within 3%,
 *   23/23 within 5% (antialiased input) and ≈0.9% on browser-like input.
 *
 * DESIGN INVARIANTS
 *   1. The sheet edge is a LOCAL STEP LINE, not a region. For every side we
 *      search straight lines (pa, pb endpoints) and score each sample as
 *      min(|near-in − near-out|, |near-in − far-out|) over luma AND warmth.
 *      The far-out window makes the colour have to STAY changed beyond the
 *      line, which rejects table rules and shaded rows (paper on both sides).
 *   2. Inside must be paper margin. A sample scores 0 when its near-in
 *      window is darker than the paper 10–16 px further in (by inDrop) or
 *      far below the sheet's paper median (paperDrop). The local test is the
 *      important one: thermal paper is grey and curls dark at the top, so an
 *      absolute floor alone rejected real edges.
 *   3. Score is robust: mean of the lowest `robustFrac` of samples. A tilted
 *      line that only half-overlaps the true edge collapses; a true edge with
 *      a finger/stapler/fold over a few samples is barely affected. Do not
 *      replace with a plain mean or max.
 *   4. Content = GLYPHS, not "dark pixels". Dark-vs-local-mean pixels →
 *      8-connected components → character-sized (≤ glyphMax px) → kept only
 *      when the 16px cell neighbourhood has enough other glyphs. Table rules,
 *      QR blocks, desk grain, floor and legs are all excluded by construction.
 *   5. Side selection = innermost clean, fully covered step. Among
 *      candidates with score ≥ tau: (a) keep those with (almost) no glyph
 *      pixels in the band just outside the line — counted in a band, not to
 *      the frame edge, so a second sheet further out does not disqualify the
 *      real edge; (b) compute coverage = fraction of samples that
 *      individually clear 0.4 × best; keep ≥ 0.8, else ≥ 0.6, else near-best
 *      — this is what stops a line that veers inward over the last third of
 *      a side (it passes the robust score but covers only ~0.7); (c) take the
 *      one nearest the glyph bbox. Never "strongest" (that is the floor or a
 *      desk edge) and never a plain bbox expansion.
 *   6. Any side not found ⇒ this detector returns null and the v12 pipeline
 *      below is the fallback. Do not fill a missing side with the frame edge
 *      inside this function.
 *   7. Known hard cases we accept: a second sheet/receipt touching the
 *      search range (its glyphs count as "outside text"), and torn thermal
 *      tops with no straight edge.
 *   8. Parameters below were swept against the fixtures both as PIL
 *      antialiased downscales and as browser-like aliased downscales; the
 *      shipped set is the best on both. Tune them with the node harness,
 *      never by eye on one photo.
 *   9. Input resampling matters: thin print at 520 px is one pixel wide. The
 *      caller must downscale with an area filter (see detectPageCorners —
 *      stepped halving), never a single-pass drawImage from 4000 px.
 * =========================================================================== */

/** [Fable 5.1] Tunables for the step-edge detector (see invariants above). */
const STEP_EDGE_PARAMS = {
  // Glyph mask (invariant 4)
  darkCut: 170, // absolute luma ceiling for "ink"
  paperFloor: 140, // local mean must be paper-ish for a pixel to count as ink
  contrast: 35, // ink must be this much darker than its 15×15 local mean
  localRad: 7,
  glyphMax: 14, // component bbox side ≤ this ⇒ character-sized
  cell: 16,
  minCompsCell: 3, // glyphs in the cell itself …
  minCompsNbhd: 6, // … or in its 3×3 cell neighbourhood
  // Step sampling (invariants 1–2)
  d0: 2,
  d1: 6, // near windows: 2–6 px either side of the line
  f0: 10,
  f1: 16, // far-out window: 10–16 px outside
  wW: 2.0, // warmth (R−B) weight relative to luma
  cap: 24, // per-sample saturation so one hard shadow cannot dominate
  paperDrop: 45, // near-inside luma may not fall this far below the sheet's paper median
  inDrop: 18, // … nor this far below the paper 10–16 px further in (a print line would)
  // Line search (invariants 3, 5)
  step: 2, // endpoint grid in px
  maxTilt: 0.3, // |pb − pa| ≤ 0.3 × side length
  samples: 48,
  robustFrac: 0.7,
  tauAbs: 3.5,
  tauRel: 0.4, // tau = max(tauAbs, tauRel × best score on that side)
  inner: 0.5, // search reaches 50% of the glyph bbox into the content
  outer: 0.25, // … and 25% of the frame outside it
  outBand: 0.15, // "outside glyphs" are counted only within 15% of the side just past the line
  outTolAbs: 3,
  outTolRel: 0.03, // allowed outside-glyph pixels: max(3, 3% of all glyph px)
  coverRel: 0.4, // a sample "covers" the edge when ≥ 0.4 × best score on that side
  coverMin: 0.8, // nearest line with ≥ 80% coverage wins …
  coverMin2: 0.6, // … else ≥ 60% (torn / occluded edges) …
  coverSlack: 0.1, // … else near-best coverage
};

function blur3(src, w, h) {
  const tmp = new Float32Array(w * h);
  const out = new Float32Array(w * h);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const x0 = Math.max(0, x - 1);
      const x1 = Math.min(w - 1, x + 1);
      tmp[y * w + x] = (src[y * w + x0] + src[y * w + x] + src[y * w + x1]) / 3;
    }
  }
  for (let y = 0; y < h; y += 1) {
    const y0 = Math.max(0, y - 1);
    const y1 = Math.min(h - 1, y + 1);
    for (let x = 0; x < w; x += 1) {
      out[y * w + x] = (tmp[y0 * w + x] + tmp[y * w + x] + tmp[y1 * w + x]) / 3;
    }
  }
  return out;
}

/** Column prefix sums: c[(y+1)*w + x] = Σ src[0..y][x]. */
function prefixY(src, w, h) {
  const c = new Float64Array(w * (h + 1));
  for (let x = 0; x < w; x += 1) {
    let s = 0;
    for (let y = 0; y < h; y += 1) {
      s += src[y * w + x];
      c[(y + 1) * w + x] = s;
    }
  }
  return c;
}

/** Row prefix sums: c[y*(w+1) + x+1] = Σ src[y][0..x]. */
function prefixX(src, w, h) {
  const c = new Float64Array((w + 1) * h);
  for (let y = 0; y < h; y += 1) {
    let s = 0;
    for (let x = 0; x < w; x += 1) {
      s += src[y * w + x];
      c[y * (w + 1) + x + 1] = s;
    }
  }
  return c;
}

/**
 * [Fable 5.1] Glyph mask + bbox (invariant 4).
 * Returns null when the frame has no printed text cluster — the caller then
 * falls back to the v12 pipeline.
 */
function buildGlyphMask(L0, w, h, p) {
  // Integral image of raw luma for the 15×15 local mean.
  const integ = new Float64Array((w + 1) * (h + 1));
  for (let y = 0; y < h; y += 1) {
    let row = 0;
    for (let x = 0; x < w; x += 1) {
      row += L0[y * w + x];
      integ[(y + 1) * (w + 1) + x + 1] = integ[y * (w + 1) + x + 1] + row;
    }
  }
  const rad = p.localRad;
  const localMean = (x, y) => {
    const x0 = Math.max(0, x - rad);
    const x1 = Math.min(w - 1, x + rad);
    const y0 = Math.max(0, y - rad);
    const y1 = Math.min(h - 1, y + rad);
    const S =
      integ[(y1 + 1) * (w + 1) + x1 + 1] -
      integ[y0 * (w + 1) + x1 + 1] -
      integ[(y1 + 1) * (w + 1) + x0] +
      integ[y0 * (w + 1) + x0];
    return S / ((x1 - x0 + 1) * (y1 - y0 + 1));
  };

  const dark = new Uint8Array(w * h);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const i = y * w + x;
      const m = localMean(x, y);
      dark[i] =
        L0[i] < m - p.contrast && L0[i] < p.darkCut && m > p.paperFloor ? 1 : 0;
    }
  }

  // 8-connected components; keep only character-sized blobs.
  const lab = new Int32Array(w * h).fill(-1);
  const qx = new Int32Array(w * h);
  const qy = new Int32Array(w * h);
  const compPix = new Int32Array(w * h);
  const comps = [];
  let pixStart = 0;
  let ncomp = 0;
  for (let ys = 0; ys < h; ys += 1) {
    for (let xs = 0; xs < w; xs += 1) {
      const i0 = ys * w + xs;
      if (!dark[i0] || lab[i0] >= 0) continue;
      let qh = 0;
      let qt = 0;
      qx[qt] = xs;
      qy[qt] = ys;
      qt += 1;
      lab[i0] = ncomp;
      let minx = xs;
      let maxx = xs;
      let miny = ys;
      let maxy = ys;
      while (qh < qt) {
        const x = qx[qh];
        const y = qy[qh];
        qh += 1;
        for (let dy = -1; dy <= 1; dy += 1) {
          for (let dx = -1; dx <= 1; dx += 1) {
            const xx = x + dx;
            const yy = y + dy;
            if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
            const j = yy * w + xx;
            if (!dark[j] || lab[j] >= 0) continue;
            lab[j] = ncomp;
            qx[qt] = xx;
            qy[qt] = yy;
            qt += 1;
            if (xx < minx) minx = xx;
            if (xx > maxx) maxx = xx;
            if (yy < miny) miny = yy;
            if (yy > maxy) maxy = yy;
          }
        }
      }
      const bw = maxx - minx + 1;
      const bh = maxy - miny + 1;
      if (qt >= 2 && bw <= p.glyphMax && bh <= p.glyphMax) {
        comps.push({
          start: pixStart,
          len: qt,
          cx: (minx + maxx) / 2,
          cy: (miny + maxy) / 2,
        });
        for (let k = 0; k < qt; k += 1) compPix[pixStart + k] = qy[k] * w + qx[k];
        pixStart += qt;
      }
      ncomp += 1;
    }
  }
  if (comps.length < 10) return null;

  // Glyphs come in words and lines: drop isolated specks (desk grain, dust).
  const cell = p.cell;
  const gw = Math.ceil(w / cell);
  const gh = Math.ceil(h / cell);
  const ccount = new Uint16Array(gw * gh);
  for (const c of comps) {
    ccount[((c.cy / cell) | 0) * gw + ((c.cx / cell) | 0)] += 1;
  }
  const keep = new Uint8Array(gw * gh);
  for (let gy = 0; gy < gh; gy += 1) {
    for (let gx = 0; gx < gw; gx += 1) {
      let sum = 0;
      for (let dy = -1; dy <= 1; dy += 1) {
        for (let dx = -1; dx <= 1; dx += 1) {
          const nx = gx + dx;
          const ny = gy + dy;
          if (nx < 0 || ny < 0 || nx >= gw || ny >= gh) continue;
          sum += ccount[ny * gw + nx];
        }
      }
      keep[gy * gw + gx] =
        ccount[gy * gw + gx] >= p.minCompsCell || sum >= p.minCompsNbhd ? 1 : 0;
    }
  }
  const text = new Uint8Array(w * h);
  const xs = [];
  const ys = [];
  let total = 0;
  for (const c of comps) {
    if (!keep[((c.cy / cell) | 0) * gw + ((c.cx / cell) | 0)]) continue;
    for (let k = 0; k < c.len; k += 1) {
      const i = compPix[c.start + k];
      text[i] = 1;
      xs.push(i % w);
      ys.push((i / w) | 0);
    }
    total += c.len;
  }
  if (total < 60) return null;

  return {
    text,
    dark,
    total,
    bx0: percentile(xs, 0.005),
    bx1: percentile(xs, 0.995),
    by0: percentile(ys, 0.005),
    by1: percentile(ys, 0.995),
  };
}

/**
 * [Fable 5.1] Primary detector. Returns the 4 corners (TL, TR, BR, BL) or
 * null when any side has no clean step line (invariant 6).
 */
function detectViaStepEdgeLines(imageData, params = STEP_EDGE_PARAMS) {
  const p = params;
  const w = imageData.width;
  const h = imageData.height;
  const data = imageData.data;

  const L0 = new Float32Array(w * h);
  const W0 = new Float32Array(w * h);
  for (let i = 0, q = 0; i < data.length; i += 4, q += 1) {
    L0[q] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    W0[q] = data[i] - data[i + 2];
  }
  const L = blur3(L0, w, h);
  const W = blur3(W0, w, h);

  const glyphs = buildGlyphMask(L0, w, h, p);
  if (!glyphs) return null;
  const { text, dark, total, bx0, bx1, by0, by1 } = glyphs;

  // Paper reference = median luma of non-ink pixels inside the glyph bbox.
  const smp = [];
  for (let y = Math.round(by0); y <= by1; y += 3) {
    for (let x = Math.round(bx0); x <= bx1; x += 3) {
      const i = y * w + x;
      if (!dark[i]) smp.push(L0[i]);
    }
  }
  if (smp.length < 16) return null;
  const paperMin = percentile(smp, 0.5) - p.paperDrop;

  const cTy = prefixY(text, w, h);
  const cTx = prefixX(text, w, h);
  const cLy = prefixY(L, w, h);
  const cWy = prefixY(W, w, h);
  const cLx = prefixX(L, w, h);
  const cWx = prefixX(W, w, h);

  const meanY = (c, x, ya, yb) => {
    const a = Math.max(0, ya);
    const b = Math.min(h - 1, yb);
    if (b < a) return null;
    return (c[(b + 1) * w + x] - c[a * w + x]) / (b - a + 1);
  };
  const meanX = (c, y, xa, xb) => {
    const a = Math.max(0, xa);
    const b = Math.min(w - 1, xb);
    if (b < a) return null;
    return (c[y * (w + 1) + b + 1] - c[y * (w + 1) + a]) / (b - a + 1);
  };

  // Step sample for a horizontal line through (x, y); sgn=+1 ⇒ inside is below.
  function sampleH(x, y, sgn) {
    const inA = Math.min(y + sgn * p.d0, y + sgn * p.d1);
    const inB = Math.max(y + sgn * p.d0, y + sgn * p.d1);
    const outA = Math.min(y - sgn * p.d1, y - sgn * p.d0);
    const outB = Math.max(y - sgn * p.d1, y - sgn * p.d0);
    const farA = Math.min(y - sgn * p.f1, y - sgn * p.f0);
    const farB = Math.max(y - sgn * p.f1, y - sgn * p.f0);
    const lin = meanY(cLy, x, inA, inB);
    const lout = meanY(cLy, x, outA, outB);
    if (lin == null || lout == null) return null;
    // invariant 2: near-inside must be paper margin.
    const lfin = meanY(cLy, x, Math.min(y + sgn * p.f0, y + sgn * p.f1), Math.max(y + sgn * p.f0, y + sgn * p.f1));
    if (lfin != null && lin < lfin - p.inDrop) return 0;
    if (lin < paperMin) return 0;
    const win = meanY(cWy, x, inA, inB);
    const wout = meanY(cWy, x, outA, outB);
    const step = Math.abs(lin - lout) + p.wW * Math.abs(win - wout);
    let far = step;
    const lfar = meanY(cLy, x, farA, farB);
    if (lfar != null) {
      const wfar = meanY(cWy, x, farA, farB);
      far = Math.abs(lin - lfar) + p.wW * Math.abs(win - wfar);
    }
    return Math.min(p.cap, Math.min(step, far));
  }
  // Same for a vertical line through (x, y); sgn=+1 ⇒ inside is to the right.
  function sampleV(x, y, sgn) {
    const inA = Math.min(x + sgn * p.d0, x + sgn * p.d1);
    const inB = Math.max(x + sgn * p.d0, x + sgn * p.d1);
    const outA = Math.min(x - sgn * p.d1, x - sgn * p.d0);
    const outB = Math.max(x - sgn * p.d1, x - sgn * p.d0);
    const farA = Math.min(x - sgn * p.f1, x - sgn * p.f0);
    const farB = Math.max(x - sgn * p.f1, x - sgn * p.f0);
    const lin = meanX(cLx, y, inA, inB);
    const lout = meanX(cLx, y, outA, outB);
    if (lin == null || lout == null) return null;
    const lfin = meanX(cLx, y, Math.min(x + sgn * p.f0, x + sgn * p.f1), Math.max(x + sgn * p.f0, x + sgn * p.f1));
    if (lfin != null && lin < lfin - p.inDrop) return 0;
    if (lin < paperMin) return 0;
    const win = meanX(cWx, y, inA, inB);
    const wout = meanX(cWx, y, outA, outB);
    const step = Math.abs(lin - lout) + p.wW * Math.abs(win - wout);
    let far = step;
    const lfar = meanX(cLx, y, farA, farB);
    if (lfar != null) {
      const wfar = meanX(cWx, y, farA, farB);
      far = Math.abs(lin - lfar) + p.wW * Math.abs(win - wfar);
    }
    return Math.min(p.cap, Math.min(step, far));
  }

  /**
   * Line search for one side. axis "h": y(x) over x∈[a0,a1]; axis "v": x(y)
   * over y∈[a0,a1]. Endpoint params range over [r0,r1]. contentEdge is the
   * glyph bbox edge used for the "nearest" tie-break (invariant 5).
   */
  function searchLine(axis, a0, a1, r0, r1, sgn, contentEdge) {
    const n = p.samples;
    const span = Math.max(1, a1 - a0);
    const maxTilt = p.maxTilt * span;
    const cands = [];
    const smps = new Float32Array(n + 1);
    const bandPx = Math.round(p.outBand * (axis === "h" ? h : w));
    for (let pa = r0; pa <= r1; pa += p.step) {
      for (let pb = r0; pb <= r1; pb += p.step) {
        if (Math.abs(pb - pa) > maxTilt) continue;
        let cnt = 0;
        let inFrame = 0;
        let outText = 0;
        for (let k = 0; k <= n; k += 1) {
          const t = k / n;
          const ai = Math.round(a0 + span * t);
          const ri = Math.round(pa + (pb - pa) * t);
          let s = null;
          if (axis === "h") {
            if (ai >= 0 && ai < w) {
              const rc = Math.min(h, Math.max(0, ri));
              // glyph pixels just outside the line (within bandPx): a text
              // row posing as the edge has them, a far second sheet does not
              const r2 = sgn > 0 ? Math.max(0, rc - bandPx) : Math.min(h, rc + bandPx);
              outText += sgn > 0
                ? cTy[rc * w + ai] - cTy[r2 * w + ai]
                : cTy[r2 * w + ai] - cTy[rc * w + ai];
            }
            cnt += 1;
            if (ri < 1 || ri >= h - 1 || ai < 0 || ai >= w) continue;
            s = sampleH(ai, ri, sgn);
          } else {
            if (ai >= 0 && ai < h) {
              const rc = Math.min(w, Math.max(0, ri));
              const r2 = sgn > 0 ? Math.max(0, rc - bandPx) : Math.min(w, rc + bandPx);
              outText += sgn > 0
                ? cTx[ai * (w + 1) + rc] - cTx[ai * (w + 1) + r2]
                : cTx[ai * (w + 1) + r2] - cTx[ai * (w + 1) + rc];
            }
            cnt += 1;
            if (ri < 1 || ri >= w - 1 || ai < 0 || ai >= h) continue;
            s = sampleV(ri, ai, sgn);
          }
          if (s == null) continue;
          smps[inFrame] = s;
          inFrame += 1;
        }
        if (inFrame < cnt * 0.6) continue;
        // invariant 3: robust lower-fraction mean
        const sub = smps.subarray(0, inFrame);
        sub.sort();
        const take = Math.max(1, Math.round(inFrame * p.robustFrac));
        let sum = 0;
        for (let k = 0; k < take; k += 1) sum += sub[k];
        cands.push({
          pa,
          pb,
          score: sum / take,
          dist: Math.abs((pa + pb) / 2 - contentEdge),
          outText,
        });
      }
    }
    if (!cands.length) return null;
    let M = 0;
    for (const c of cands) if (c.score > M) M = c.score;
    const tau = Math.max(p.tauAbs, p.tauRel * M);
    const strong = cands.filter((c) => c.score >= tau);
    if (!strong.length) return null;
    // invariant 5: cleanest outside, then full coverage, then nearest.
    let minOut = Infinity;
    for (const c of strong) if (c.outText < minOut) minOut = c.outText;
    const tol = Math.max(p.outTolAbs, p.outTolRel * total);
    const cleanAll = strong.filter((c) => c.outText <= minOut + tol);
    // Coverage = fraction of samples that individually look like the edge.
    // A line tilted inward at one end tracks the edge for ~70% of the side
    // and passes the robust score, but covers ~0.7; the true edge ~1.0.
    const perThr = Math.max(p.tauAbs, p.coverRel * M);
    for (const c of cleanAll) {
      let cnt = 0;
      let good = 0;
      for (let k = 0; k <= n; k += 1) {
        const t = k / n;
        const ai = Math.round(a0 + span * t);
        const ri = Math.round(c.pa + (c.pb - c.pa) * t);
        let s2 = null;
        if (axis === "h") {
          if (ri < 1 || ri >= h - 1 || ai < 0 || ai >= w) continue;
          s2 = sampleH(ai, ri, sgn);
        } else {
          if (ri < 1 || ri >= w - 1 || ai < 0 || ai >= h) continue;
          s2 = sampleV(ri, ai, sgn);
        }
        if (s2 == null) continue;
        cnt += 1;
        if (s2 >= perThr) good += 1;
      }
      c.cover = cnt ? good / cnt : 0;
    }
    let maxCover = 0;
    for (const c of cleanAll) if (c.cover > maxCover) maxCover = c.cover;
    // Absolute bars first (a torn thermal edge still covers ~60–80% of its
    // length); only when nothing clears them, fall back to near-best coverage.
    let clean = [];
    for (const bar of [p.coverMin, p.coverMin2]) {
      clean = cleanAll
        .filter((c) => c.cover >= bar)
        .sort((a, b) => a.dist - b.dist);
      if (clean.length) break;
    }
    if (!clean.length) {
      clean = cleanAll
        .filter((c) => c.cover >= maxCover - p.coverSlack)
        .sort((a, b) => a.dist - b.dist);
    }
    let best = clean[0];
    // Local refine: a neighbour (±4 px per endpoint) with a higher score.
    for (const c of cands) {
      if (
        Math.abs(c.pa - best.pa) <= 4 &&
        Math.abs(c.pb - best.pb) <= 4 &&
        c.score > best.score
      ) {
        best = c;
      }
    }
    return best;
  }

  const inY = p.inner * (by1 - by0);
  const inX = p.inner * (bx1 - bx0);
  const top = searchLine(
    "h", bx0, bx1, Math.round(-p.outer * h), Math.round(by0 + inY), +1, by0
  );
  const bot = searchLine(
    "h", bx0, bx1, Math.round(by1 - inY), Math.round(h + p.outer * h), -1, by1
  );
  const left = searchLine(
    "v", by0, by1, Math.round(-p.outer * w), Math.round(bx0 + inX), +1, bx0
  );
  const right = searchLine(
    "v", by0, by1, Math.round(bx1 - inX), Math.round(w + p.outer * w), -1, bx1
  );
  if (!top || !bot || !left || !right) return null; // invariant 6

  const T = [{ x: bx0, y: top.pa }, { x: bx1, y: top.pb }];
  const B = [{ x: bx0, y: bot.pa }, { x: bx1, y: bot.pb }];
  const Lf = [{ x: left.pa, y: by0 }, { x: left.pb, y: by1 }];
  const R = [{ x: right.pa, y: by0 }, { x: right.pb, y: by1 }];

  function inter(p1, p2, p3, p4) {
    const d = (p1.x - p2.x) * (p3.y - p4.y) - (p1.y - p2.y) * (p3.x - p4.x);
    if (Math.abs(d) < 1e-9) return null;
    const a = p1.x * p2.y - p1.y * p2.x;
    const b = p3.x * p4.y - p3.y * p4.x;
    return {
      x: (a * (p3.x - p4.x) - (p1.x - p2.x) * b) / d,
      y: (a * (p3.y - p4.y) - (p1.y - p2.y) * b) / d,
    };
  }
  const tl = inter(T[0], T[1], Lf[0], Lf[1]);
  const tr = inter(T[0], T[1], R[0], R[1]);
  const br = inter(B[0], B[1], R[0], R[1]);
  const bl = inter(B[0], B[1], Lf[0], Lf[1]);
  if (!tl || !tr || !br || !bl) return null;
  const quad = clampCorners([tl, tr, br, bl], w, h, 0);
  return isConvexQuad(quad) ? quad : null;
}
/* ====================== end [Fable 5.1 · Step-edge sheet detector] ====== */

/** Bumped when corner logic changes — shown in the straighten dialog. */
export const PAGE_DETECT_VERSION = "v14-stepEdge";

/**
 * @param {ImageData} imageData
 * @returns {[{x:number,y:number}, ...] | null}
 */
export function detectPageCornersFromImageData(imageData) {
  const w = imageData.width;
  const h = imageData.height;
  if (w < 48 || h < 48) return null;

  // [Fable 5.1] Primary: step-edge line search (see block above). The v12
  // mask/ray pipeline below is only the fallback when it returns null.
  const stepEdge = detectViaStepEdgeLines(imageData);
  if (stepEdge) return stepEdge;

  const gray0 = toGray(imageData.data, w, h);
  const gray = boxBlur(gray0, w, h, 1);
  const variance = localVariance(gray, w, h, 2);
  const mag = sobelMagnitude(gray, w, h);
  const ink = buildInkOnPaperMask(gray, w, h);

  // Paper↔background outline + content/ink fallbacks; rank picks the winner.
  // Do not sobel-snap the paper-bg result — that pulls corners onto table rules.
  const primaryRaw = detectViaPaperBackgroundEdge(
    imageData.data,
    gray,
    mag,
    ink.mask,
    w,
    h,
    variance
  );
  let primary = primaryRaw ? clampCorners(primaryRaw, w, h) : null;
  if (primary) {
    // Extend into blank margins without mag-snapping to tables.
    primary = expandTopBottomToPaperBorder(
      primary,
      gray,
      mag,
      w,
      h,
      ink.mask
    );
    primary = expandLeftRightToInk(primary, gray, w, h, ink.mask);
    primary = clampCorners(primary, w, h);
  }

  const polishedFallbacks = [
    detectViaInkHaloCorners(gray, mag, ink.mask, w, h),
    detectViaTexturedPaper(gray, variance, w, h),
    detectViaInkBounds(gray, w, h),
    detectViaRadialEdges(gray, mag, ink.mask, w, h),
  ]
    .filter(Boolean)
    .map((c) => {
      let out = refineCornersByEdgeLines(c, mag, w, h);
      out = snapCornersToEdges(out, mag, w, h);
      out = expandTopBottomToPaperBorder(out, gray, mag, w, h, ink.mask);
      out = expandLeftRightToInk(out, gray, w, h, ink.mask);
      out = refineCornersMaximizeEdgeEnergy(out, mag, w, h);
      return clampCorners(out, w, h);
    })
    .filter((c) => isConvexQuad(c));

  const perspective = [primary, ...polishedFallbacks].filter(
    (c) => c && isConvexQuad(c)
  );

  let best = pickBestCorners(perspective, w, h, ink.mask);
  if (best) {
    // Final margin recovery after ranking (winner may still be slightly tight).
    best = expandTopBottomToPaperBorder(best, gray, mag, w, h, ink.mask);
    best = expandLeftRightToInk(best, gray, w, h, ink.mask);
    return clampCorners(best, w, h);
  }

  return pickBestCorners(
    [detectViaBlob(gray, variance, w, h), detectViaEdgeProjection(gray, w, h)],
    w,
    h,
    ink.mask
  );
}

/**
 * Detect corners from an HTMLImageElement / ImageBitmap.
 * Downscales for speed, maps corners back to natural size.
 */
export function detectPageCorners(image, { maxSide = 520 } = {}) {
  const srcW = image.naturalWidth || image.width;
  const srcH = image.naturalHeight || image.height;
  if (!srcW || !srcH) return null;

  const scale = Math.min(1, maxSide / Math.max(srcW, srcH));
  const w = Math.max(48, Math.round(srcW * scale));
  const h = Math.max(48, Math.round(srcH * scale));

  // Fable invariant 9: area-filtered downscale. A single drawImage from a
  // 4000 px photo to 520 px is point-sampled in every browser and drops the
  // one-pixel print strokes the detector's glyph mask depends on. Halve the
  // image step by step (each halving is a true 2×2 average) until the final
  // factor is < 2, then do the last resize with high-quality smoothing.
  let src = image;
  let curW = srcW;
  let curH = srcH;
  while (curW / 2 >= w && curH / 2 >= h) {
    const nw = Math.round(curW / 2);
    const nh = Math.round(curH / 2);
    const c = document.createElement("canvas");
    c.width = nw;
    c.height = nh;
    const cx = c.getContext("2d");
    if (!cx) return null;
    cx.imageSmoothingEnabled = true;
    cx.imageSmoothingQuality = "high";
    cx.drawImage(src, 0, 0, curW, curH, 0, 0, nw, nh);
    src = c;
    curW = nw;
    curH = nh;
  }

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(src, 0, 0, curW, curH, 0, 0, w, h);
  let imageData;
  try {
    imageData = ctx.getImageData(0, 0, w, h);
  } catch {
    return null;
  }

  const small = detectPageCornersFromImageData(imageData);
  if (!small) return null;

  const sx = srcW / w;
  const sy = srcH / h;
  return small.map((p) => ({
    x: Math.min(srcW - 1, Math.max(0, p.x * sx)),
    y: Math.min(srcH - 1, Math.max(0, p.y * sy)),
  }));
}
